// @intent: Native Pixi match-3 renderer/controller for data-driven Match3LevelV1 payloads.
// @flow: Project.variables.nativeMatch3 -> native renderer -> shared engine state -> runtime probe provider.
// @stale: Keep emitted-code-free; generated code must not own match-3 board logic once this branch is active.

import '@pixi/react';
import React from 'react';
import {
  createMatch3LevelRuntime,
  type JsonObject,
  type Match3NativeRuntimePayloadV1,
  type Match3ProbeIndexMoveV1,
  type Match3ProbeObjectiveFlightV1,
  type Match3ProbeResolutionPlanV1,
  type Match3ProbeSpecialEntryV1,
  type Match3ProbeStateV1,
  type Match3ResolutionPhaseV1,
  type PlayablePointerEventType,
  type PlayablePointerHandler,
  type PlayableTweenHandle,
} from '@mintyads/playable-api';
import { getPixiTexture } from '../pixiAssets';
import { createNumericTween } from '../pixiTween';
import type { RuntimeProbeTweenRegistry } from '../../probe/runtimeProbeTweens';
import { beginMatch3Drag, finishMatch3Drag, type Match3DragState } from './Match3Input.js';
import { computeMatch3CellRects } from './Match3Layout.js';
import { createMatch3RuntimeViewModel } from './Match3Runtime.js';
import { match3TelemetryEvents } from './Match3Telemetry.js';
import { clearPop, collectChime, denyBuzz, initSfxOnGesture, swapBlip, winSting } from './match3Sfx.js';
import { truncateTitle } from './match3TitleUtils.js';

const SWAP_MS = 250;
const CLEAR_POP_MS = 90;
const CLEAR_FADE_MS = 200;
const REFILL_SETTLE_MS = 150;
const REFILL_RESYNC_MS = 80;
const FLOWER_FLY_MS = 420;
// Fraction of boardHeight above boardTopY where new tiles spawn before dropping in (uniform across all rows).
const REFILL_SPAWN_ABOVE_RATIO = 0.4;
const REFILL_DROP_MS = 220;
// Alpha applied to a flower sprite once its objective has been collected (decorative dim state).
const COLLECTED_FLOWER_ALPHA = 0.38;
// Corner radius (in design px) for programmatic per-cell socket underlay rounded rects.
const CELL_SOCKET_CORNER_RADIUS_RATIO = 0.18;
// Fill color for cell socket underlay (semi-transparent dark overlay on boardPanel backdrop).
const CELL_SOCKET_FILL = 0x000000;
const CELL_SOCKET_ALPHA = 0.18;
// Fraction of board inset (design px) to add around the board for the boardPanel backdrop.
const BOARD_PANEL_INSET_EXTRA_RATIO = 0.08;
// Programmatic board backdrop fill (dark semi-opaque) — replaces generated boardPanel image whose baked cell
// grid conflicts with our programmatic sockets (Fix 1: two competing grids defect).
const BOARD_PANEL_FILL = 0x1a1040;
const BOARD_PANEL_ALPHA = 0.82;
const BOARD_PANEL_BORDER_COLOR = 0x7744cc;
const BOARD_PANEL_BORDER_ALPHA = 0.55;
const BOARD_PANEL_BORDER_WIDTH = 2;
const BOARD_PANEL_CORNER_RADIUS_RATIO = 0.045;
// Programmatic HUD pill fill (Fix 3: replace stretched hudPanel image with legible objective counter).
const HUD_PILL_FILL = 0x1a1040;
const HUD_PILL_ALPHA = 0.82;
const HUD_PILL_BORDER_COLOR = 0x7744cc;
const HUD_PILL_BORDER_ALPHA = 0.55;
const HUD_PILL_BORDER_WIDTH = 2;
// Shake animation constants for deny feedback on static-target swaps (Fix 4).
// animateDenyShake uses 3 steps: right → left → right → settle (4 offsets total).
const SHAKE_OFFSET_PX = 4;
const SHAKE_STEP_MS = 40;
// A3: Micro-animation constants.
// (a) Swap overshoot-settle: use backOut easing which provides ~6% overshoot inherently; total 250-300ms.
// (b) Refill landing squash: scaleY 0.92 / scaleX 1.08 → 1.0 over ~90ms chained after drop tween.
const REFILL_SQUASH_SCALE_Y = 0.92;
const REFILL_SQUASH_SCALE_X = 1.08;
const REFILL_SQUASH_MS = 90;
// (c) HUD counter bounce: scale 1→1.25→1 over 160ms when collect flight completes.
const HUD_BOUNCE_PEAK = 1.25;
const HUD_BOUNCE_HALF_MS = 80; // 80ms up + 80ms down = 160ms total
// (d) Flower stage-growth pop: scale 1→1.3→1 on stage advance (not final collect).
const FLOWER_GROWTH_PEAK = 1.3;
const FLOWER_GROWTH_HALF_MS = 90; // 90ms up + 90ms down = 180ms total
// A2: Clear particle constants — mini-copies of the cleared tile's texture radiate from cell center.
const CLEAR_FX_COUNT_MIN = 5;
const CLEAR_FX_COUNT_MAX = 7;
const CLEAR_FX_SCALE = 0.25;
const CLEAR_FX_MS = 500;
const CLEAR_FX_GRAVITY = 0.18; // fraction of tile height added to y target (slight downward drift)
const CLEAR_FX_TRAVEL = 1.4; // max travel distance in tile widths
// Cap on concurrently mounted clear-fx sprites — skips new particles when at limit.
const MAX_CLEAR_FX_SPRITES = 24;
// Monotonic counter for clear-fx batch ids — prevents layer-id collisions when the same cell is
// cleared twice (e.g. re-spawned tile) within a single game session.
let clearFxBatchCounter = 0;

// A4: Cascade combo popup constants.
// skin/i18n slot later, Wave B
const COMBO_POPUP_LABELS = ['', '', 'Nice!', 'Great!', 'Amazing!'] as const; // index = depth; 0,1 unused
const COMBO_POPUP_SCALE_IN_MS = 150;
const COMBO_POPUP_HOLD_MS = 350;
const COMBO_POPUP_FADE_MS = 200;

// Б1: Special-tile visual constants.
// Rocket line-sweep: a flash rect sweeps the full row or column in ~250ms.
const BLAST_SWEEP_MS = 250;
// Blast-fx layers use a monotonic batch counter so ids don't collide when the same cell detonates twice.
let blastFxBatchCounter = 0;

// A5: Win celebration constants.
// ~20 confetti pieces fall/appear during win state.
const CONFETTI_COUNT = 20;
// CTA pulse: scale 1 → 1.06 → 1 loop; each half-cycle is this duration.
const CTA_PULSE_HALF_MS = 350;

export interface Match3NativeRendererProps {
  readonly payload: Match3NativeRuntimePayloadV1;
  readonly designViewport: { readonly width: number; readonly height: number };
  // @intent Title text to display on the left side of the HUD band (P16c Fix 3: replaces scene-graph
  //         native-match3-title which was at y:39, invisible under the phone-frame notch).
  readonly designTitle?: string | undefined;
  readonly registerTarget: (layerId: string, target: Record<string, unknown>) => () => void;
  readonly onLayerSelected: (layerId: string | null) => void;
  readonly onInput: (type: PlayablePointerEventType, handler: PlayablePointerHandler) => () => void;
  readonly emit: (name: string, data?: JsonObject) => void;
  readonly probeStateProviders: Map<string, () => unknown>;
  readonly probeTweenRegistry?: RuntimeProbeTweenRegistry | undefined;
}

interface NativeRuntimeMeta {
  ended: boolean;
  resolving: boolean;
  resolutionPhase: Match3ResolutionPhaseV1;
  acceptedMoveSeq: number;
  rejectedMoveSeq: number;
  lastAcceptedMove: Match3ProbeIndexMoveV1 | null;
  lastRejectedMove: Match3ProbeIndexMoveV1 | null;
  lastRepaintIndexes: number[];
  lastResolutionPlan: Match3ProbeResolutionPlanV1 | null;
  objectiveFlights: Match3ProbeObjectiveFlightV1[];
}

interface NativeMatch3Hooks {
  firstMoveDesign: () => { from: { x: number; y: number }; to: { x: number; y: number } } | null;
  nextValidSwapDesign: () => { from: { x: number; y: number }; to: { x: number; y: number } } | null;
  boardTileCount: () => number;
  objectiveRemaining: () => number;
  ctaShown: () => boolean;
}

function asJson(data: Record<string, unknown>): JsonObject {
  return JSON.parse(JSON.stringify(data)) as JsonObject;
}

function finitePerformanceNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
}

function requestFrame(callback: FrameRequestCallback): number {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    return window.requestAnimationFrame(callback);
  }
  return setTimeout(() => { callback(finitePerformanceNow()); }, 16) as unknown as number;
}

function cancelFrame(handle: number): void {
  if (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
    window.cancelAnimationFrame(handle);
    return;
  }
  clearTimeout(handle as unknown as ReturnType<typeof setTimeout>);
}

function NativeSprite(input: {
  readonly id: string;
  readonly assetUrl: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly anchor?: { readonly x: number; readonly y: number };
  readonly visible?: boolean;
  readonly registerTarget: (layerId: string, target: Record<string, unknown>) => () => void;
  readonly onLayerSelected: (layerId: string | null) => void;
}) {
  const unregisterTargetRef = React.useRef<(() => void) | null>(null);
  const targetRef = React.useCallback((target: unknown) => {
    unregisterTargetRef.current?.();
    unregisterTargetRef.current = null;
    if (target) {
      const record = target as Record<string, unknown>;
      record.assetUrl = input.assetUrl;
      record.x = input.x;
      record.y = input.y;
      record.width = input.width;
      record.height = input.height;
      if (typeof record.getBounds !== 'function') {
        const anchorX = input.anchor?.x ?? 0.5;
        const anchorY = input.anchor?.y ?? 0.5;
        record.getBounds = () => ({
          x: input.x - input.width * anchorX,
          y: input.y - input.height * anchorY,
          width: input.width,
          height: input.height,
        });
      }
      if (record.texture && typeof record.texture === 'object') {
        (record.texture as Record<string, unknown>).assetUrl = input.assetUrl;
      }
      unregisterTargetRef.current = input.registerTarget(input.id, record);
    }
  }, [input]);
  const texture = getPixiTexture(input.assetUrl);
  const onPointerDown = React.useCallback(() => { input.onLayerSelected(input.id); }, [input]);

  if (texture) {
    return (
      <pixiSprite
        ref={targetRef}
        texture={texture}
        x={input.x}
        y={input.y}
        width={input.width}
        height={input.height}
        anchor={input.anchor ?? 0.5}
        visible={input.visible ?? true}
        eventMode="static"
        onPointerDown={onPointerDown}
      />
    );
  }

  // Fallback placeholder when texture is not yet loaded; honour the same `visible` flag so
  // sprites gated on terminal state (endcard, cta) do not flash a white rect during gameplay.
  return (
    <pixiGraphics
      ref={targetRef}
      x={input.x - input.width / 2}
      y={input.y - input.height / 2}
      visible={input.visible ?? true}
      eventMode="static"
      onPointerDown={onPointerDown}
      draw={(g) => {
        g.clear();
        if (input.visible === false) return;
        g.rect(0, 0, input.width, input.height);
        g.fill(0xffffff);
      }}
    />
  );
}

function NativeText(input: {
  readonly id: string;
  readonly text: string;
  readonly x: number;
  readonly y: number;
  readonly fontSize: number;
  readonly fill: string;
  readonly visible?: boolean;
  // Anchor for the text origin; defaults to centered (0.5) for HUD counter/CTA labels. The title
  // passes {x:0, y:0.5} (left-aligned) — its x math assumes the text grows rightward from hud.x+pad.
  readonly anchor?: number | { readonly x: number; readonly y: number };
  readonly registerTarget: (layerId: string, target: Record<string, unknown>) => () => void;
}) {
  const unregisterTargetRef = React.useRef<(() => void) | null>(null);
  const targetRef = React.useCallback((target: unknown) => {
    unregisterTargetRef.current?.();
    unregisterTargetRef.current = null;
    if (target) {
      const record = target as Record<string, unknown>;
      record.text = input.text;
      record.__mintyProbe = { role: 'cta-label', flags: ['overlay', 'text', 'non-draggable'] };
      unregisterTargetRef.current = input.registerTarget(input.id, record);
    }
  }, [input]);

  return (
    <pixiText
      ref={targetRef}
      text={input.text}
      x={input.x}
      y={input.y}
      anchor={input.anchor ?? 0.5}
      visible={input.visible ?? true}
      style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: input.fontSize,
        fill: input.fill,
        fontWeight: '900',
        align: 'center',
      }}
    />
  );
}

export function Match3NativeRenderer({
  payload,
  designViewport,
  designTitle,
  registerTarget,
  onLayerSelected,
  onInput,
  emit,
  probeStateProviders,
  probeTweenRegistry,
}: Match3NativeRendererProps) {
  const runtime = React.useMemo(
    () => createMatch3LevelRuntime(payload.level, {
      seed: payload.level.runtimeSeed ?? 1337,
      ...(payload.level.script?.firstMove === undefined ? {} : { firstMove: payload.level.script.firstMove }),
    }),
    [payload.level],
  );
  const metaRef = React.useRef<NativeRuntimeMeta>({
    ended: false,
    resolving: false,
    resolutionPhase: 'idle',
    acceptedMoveSeq: 0,
    rejectedMoveSeq: 0,
    lastAcceptedMove: null,
    lastRejectedMove: null,
    lastRepaintIndexes: [],
    lastResolutionPlan: null,
    objectiveFlights: [],
  });
  const dragRef = React.useRef<Match3DragState | null>(null);
  // @intent Track in-flight flower collect animations as { index, assetUrl } tuples so a dedicated React-mounted
  //         flight sprite can be tweened independently of the home sprite (Defect C: fixes React prop override).
  // @flow animateFlowerChanges sets this; setRevision after FLOWER_FLY_MS triggers a re-render that drops them.
  //       The flight sprite layer `match-static-fly-{index}` is tweened; the home sprite stays at home visible.
  // @stale 2026-06-10 — cleared when animateFlowerChanges starts a new flight batch.
  const [flightSprites, setFlightSprites] = React.useState<
    ReadonlyArray<{ readonly index: number; readonly assetUrl: string; readonly x: number; readonly y: number; readonly width: number; readonly height: number }>
  >([]);
  const timersRef = React.useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const tweenHandlesRef = React.useRef<PlayableTweenHandle[]>([]);
  const tweenSeqRef = React.useRef(0);
  const nativeTargetsRef = React.useRef(new Map<string, Record<string, unknown>>());
  // @intent Registry of layer ids with an active drop tween (populated by animateRefillDrop, cleared per-layer
  //         on tween completion). syncTileLayersToRuntimeBoard skips these layers so the second RESYNC pass
  //         cannot snap their position back to home while the drop tween is still animating (A1 jitter fix).
  // @flow animateRefillDrop adds each dropping layerId; the tween's finished callback deletes it.
  // @stale 2026-06-11 — A1 fix: eliminates the visible y-snap during the REFILL_RESYNC_MS second sync pass.
  const droppingLayersRef = React.useRef(new Set<string>());
  // @intent Pending tween-start callbacks keyed by flight-sprite layer id, consumed deterministically when
  //         the NativeSprite mounts and calls registerNativeTarget — eliminates the RAF-before-React-commit
  //         race condition (Defect: requestFrame fires before React commits the flight sprite).
  // @flow animateFlowerChanges writes entries; registerNativeTarget drains them on mount (once per layerId).
  // @stale 2026-06-10 — entry removed immediately after the tween is started; Map stays empty between flights.
  const pendingFlightTweensRef = React.useRef(new Map<string, () => void>());
  // @intent Pending tween-start callbacks keyed by clear-fx sprite layer id (A2), drained by registerNativeTarget
  //         on mount — same mount-hook pattern as pendingFlightTweensRef to avoid RAF timing races.
  // @flow animateTileClear writes entries; registerNativeTarget drains them; entry removed after tween starts.
  // @stale 2026-06-11 — A2: clear particle pending-tween drain map.
  const pendingClearFxTweensRef = React.useRef(new Map<string, () => void>());
  // @intent Short-lived clear-fx particle sprites (A2): 5-7 mini-copies of cleared tile texture per cell.
  // @flow animateTileClear appends entries; each particle's tween finished.finally filters it out on completion.
  // @stale 2026-06-11 — A2: cleared per-particle on completion via functional state filter.
  const [clearFxSprites, setClearFxSprites] = React.useState<
    ReadonlyArray<{ readonly layerId: string; readonly assetUrl: string; readonly x: number; readonly y: number; readonly width: number; readonly height: number }>
  >([]);
  // A4: Combo popup — short-lived NativeText layer showing cascade depth label (e.g. "Nice!", "Great!").
  // @intent Displayed when cascade depth >= 2; unmounted after scale-in + hold + fade sequence.
  // @flow animateCascadeCombo sets this; fade-complete callback sets to null.
  // @stale 2026-06-11 — A4: cascade combo popup state.
  const [comboPopup, setComboPopup] = React.useState<{ readonly id: string; readonly label: string } | null>(null);
  // @intent Pending tween-start callbacks for combo-popup layers, drained by registerNativeTarget on mount.
  // @flow animateCascadeCombo writes entries; registerNativeTarget drains them; entry removed after tween starts.
  // @stale 2026-06-11 — A4: same mount-hook pattern as pendingClearFxTweensRef.
  const pendingComboPopupTweensRef = React.useRef(new Map<string, () => void>());
  // Б1: Special-tile state — map from cell index to kind string ('rocket' | 'bomb' | 'colorBomb').
  // @intent Tracks which board cells currently hold a special tile; used to draw programmatic overlays over tiles.
  // @flow Engine resolution plan spawnedSpecials populates this; detonatedSpecials removes entries.
  // @stale 2026-06-11 — Б1: special overlay map.
  const specialsMapRef = React.useRef(new Map<number, string>());
  // Б1: Blast-fx sprites — short-lived graphics shown during a special detonation.
  // @intent Rocket → line-sweep flash rect; bomb → radial ring; colorBomb → tile-level flashes (reuse clear fx).
  // @flow animateSpecialBlast appends entries; tween finished.finally removes each entry on completion.
  // @stale 2026-06-11 — Б1: blast effect sprites.
  const [blastFxSprites, setBlastFxSprites] = React.useState<
    ReadonlyArray<{ readonly layerId: string; readonly kind: 'sweep-h' | 'sweep-v' | 'ring'; readonly x: number; readonly y: number; readonly width: number; readonly height: number; readonly color: number }>
  >([]);
  // @intent Pending tween callbacks for blast-fx layers, drained by registerNativeTarget on mount.
  // @flow animateSpecialBlast writes entries; registerNativeTarget drains them on mount.
  // @stale 2026-06-11 — Б1: same mount-hook pattern as pendingClearFxTweensRef.
  const pendingBlastFxTweensRef = React.useRef(new Map<string, () => void>());
  // A5: Stable confetti layout computed once when win triggers (avoids Math.random in draw callback → flicker).
  // @intent Array of confetti piece rects for the win celebration graphic; populated on win, cleared on unmount.
  // @stale 2026-06-11 — A5: confetti layout ref.
  const confettiLayoutRef = React.useRef<ReadonlyArray<{ readonly x: number; readonly y: number; readonly w: number; readonly h: number; readonly color: number }>>([]);
  // @intent Whether confetti graphic is currently mounted (transitions to false after timeout on win).
  // @stale 2026-06-11 — A5: confetti visible toggle.
  const [confettiVisible, setConfettiVisible] = React.useState(false);
  // @intent Track first accepted move so presentation.banner can fade out on first interaction.
  // @flow set to true when nextMoveSeq === 1 (first accepted drag); gates banner visibility + fade.
  // @stale 2026-06-12 — presentation.banner pre-interaction banner feature.
  const [hadFirstInteraction, setHadFirstInteraction] = React.useState(false);
  // @intent Handle for the looping CTA pulse setTimeout chain; cleaned up on unmount.
  // @stale 2026-06-11 — A5: CTA pulse timer ref.
  const ctaPulseRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [revision, setRevision] = React.useState(0);
  const [visualBoard, setVisualBoard] = React.useState<readonly number[]>(() => runtime.board.cells.slice());
  const viewModel = React.useMemo(
    () => createMatch3RuntimeViewModel({ level: payload.level, skin: payload.skin, viewport: designViewport }),
    [designViewport, payload.level, payload.skin],
  );
  const cells = React.useMemo(
    () => computeMatch3CellRects(viewModel.layout.board, payload.level.board.rows, payload.level.board.cols, payload.skin.fit.tileCellFill),
    [payload.level.board.cols, payload.level.board.rows, payload.skin.fit.tileCellFill, viewModel.layout.board],
  );
  const tutorialMaskIndexes = React.useMemo(() => {
    const firstMask = payload.level.tutorial?.masks?.[0];
    if (!firstMask) return [];
    return firstMask
      .map((value, index) => (value > 0 ? index : -1))
      .filter((index) => index >= 0);
  }, [payload.level.tutorial?.masks]);
  const allCellIndexes = React.useMemo(() => cells.map((cell) => cell.tileIndex), [cells]);
  const tileAssetById = React.useMemo(
    () => new Map(payload.level.tiles.map((tile) => [tile.id, tile.assetUrl])),
    [payload.level.tiles],
  );
  React.useEffect(() => {
    // Defensive: a skin with fewer tile assets than level tile types means some tile kinds render
    // duplicated/untextured (the "invisible tile" generation bug); surface it instead of failing silently.
    if (payload.skin.slots.tileTypes.length < payload.level.tiles.length) {
      console.warn(`[match3] skin.slots.tileTypes has ${String(payload.skin.slots.tileTypes.length)} assets but the level defines ${String(payload.level.tiles.length)} tile types; tile art will repeat or go missing`);
    }
  }, [payload.level.tiles.length, payload.skin.slots.tileTypes.length]);
  const staticByIndex = React.useMemo(() => new Map(
    (payload.level.board.staticCells ?? []).map((cell) => [
      runtime.indexAt(cell.at.row, cell.at.col),
      cell,
    ]),
  ), [payload.level.board.staticCells, runtime]);
  const staticIndexSet = React.useMemo(() => new Set(runtime.staticCellIndexes()), [runtime, revision]);
  const designMove = React.useCallback((move: { readonly from: number; readonly to: number } | null) => {
    if (!move) return null;
    const from = cells[move.from]?.center;
    const to = cells[move.to]?.center;
    if (!from || !to) return null;
    return {
      from: { x: from.x, y: from.y },
      to: { x: to.x, y: to.y },
    };
  }, [cells]);
  const tupleMove = React.useCallback((move: { readonly from: number; readonly to: number } | null) => {
    if (!move) return null;
    const from = runtime.coordAt(move.from);
    const to = runtime.coordAt(move.to);
    return {
      from: [from.row, from.col] as const,
      to: [to.row, to.col] as const,
    };
  }, [runtime]);
  React.useEffect(() => {
    setVisualBoard(runtime.board.cells.slice());
  }, [runtime]);
  // Б1 (pre-placed specials): seed specialsMapRef from engine state on mount / runtime change.
  // @intent Pre-placed specials (level.specials[].at) are seeded into the engine's specialByIndex at
  //   construction time but the renderer's specialsMapRef starts empty. Without this effect the overlays
  //   for those specials never appear until a swap triggers resolveMatched. Seeding here mirrors how
  //   setVisualBoard above seeds the visual board so both are consistent from the first paint.
  // @flow runtime.getSpecialIndexes() → specialsMapRef.current refill → setRevision triggers re-render.
  // @stale 2026-06-11 — Б1: pre-placed special seed on mount.
  React.useEffect(() => {
    specialsMapRef.current = new Map(
      runtime.getSpecialIndexes().map((s) => [s.index, s.kind]),
    );
    setRevision((r) => r + 1);
  }, [runtime]);

  const updateMeta = React.useCallback((patch: Partial<NativeRuntimeMeta>, options?: { readonly render?: boolean }) => {
    metaRef.current = { ...metaRef.current, ...patch };
    if (options?.render) setRevision((current) => current + 1);
  }, []);
  const registerNativeTarget = React.useCallback((layerId: string, target: Record<string, unknown>) => {
    nativeTargetsRef.current.set(layerId, target);
    // Drain any pending flight tween for this layer now that it is mounted — deterministic alternative to RAF.
    const pendingTween = pendingFlightTweensRef.current.get(layerId);
    if (pendingTween) {
      pendingFlightTweensRef.current.delete(layerId);
      pendingTween();
    }
    // A2: Drain any pending clear-fx tween for this particle layer on mount.
    const pendingFxTween = pendingClearFxTweensRef.current.get(layerId);
    if (pendingFxTween) {
      pendingClearFxTweensRef.current.delete(layerId);
      pendingFxTween();
    }
    // A4: Drain any pending combo-popup tween for this layer on mount.
    const pendingComboTween = pendingComboPopupTweensRef.current.get(layerId);
    if (pendingComboTween) {
      pendingComboPopupTweensRef.current.delete(layerId);
      pendingComboTween();
    }
    // Б1: Drain any pending blast-fx tween for this layer on mount.
    const pendingBlastTween = pendingBlastFxTweensRef.current.get(layerId);
    if (pendingBlastTween) {
      pendingBlastFxTweensRef.current.delete(layerId);
      pendingBlastTween();
    }
    const unregister = registerTarget(layerId, target);
    return () => {
      if (nativeTargetsRef.current.get(layerId) === target) nativeTargetsRef.current.delete(layerId);
      unregister();
    };
  }, [registerTarget]);
  const setLayerProps = React.useCallback((layerId: string, props: Record<string, number | boolean>) => {
    const target = nativeTargetsRef.current.get(layerId);
    if (!target) return;
    for (const [key, value] of Object.entries(props)) {
      target[key] = value;
    }
  }, []);
  const tweenLayer = React.useCallback((
    layerId: string,
    props: Record<string, number>,
    options: { readonly durationMs: number; readonly delayMs?: number; readonly easing?: 'linear' | 'easeInQuad' | 'easeOutQuad' | 'easeInOutQuad' | 'backOut' },
  ) => {
    const target = nativeTargetsRef.current.get(layerId);
    if (!target) return;
    const handle = createNumericTween({
      id: `native-match3-${layerId}-${String(tweenSeqRef.current += 1)}`,
      target: target as Record<string, number>,
      props,
      options,
      requestFrame,
      cancelFrame,
      now: finitePerformanceNow,
      probeTargetLayerId: layerId,
      onProbeUpdate: (snapshot) => { probeTweenRegistry?.upsert(snapshot); },
    });
    tweenHandlesRef.current.push(handle);
    void handle.finished.finally(() => {
      tweenHandlesRef.current = tweenHandlesRef.current.filter((current) => current !== handle);
    }).catch(() => undefined);
  }, [probeTweenRegistry]);
  const stopRunningTweens = React.useCallback(() => {
    for (const handle of tweenHandlesRef.current) handle.stop();
    tweenHandlesRef.current = [];
  }, []);
  const scheduleResolutionStep = React.useCallback((delayMs: number, callback: () => void) => {
    const timer = setTimeout(() => {
      timersRef.current = timersRef.current.filter((current) => current !== timer);
      callback();
    }, delayMs);
    timersRef.current.push(timer);
  }, []);
  const clearScheduledResolution = React.useCallback(() => {
    for (const timer of timersRef.current) clearTimeout(timer);
    timersRef.current = [];
  }, []);

  React.useEffect(() => () => {
    clearScheduledResolution();
    stopRunningTweens();
  }, [clearScheduledResolution, stopRunningTweens]);

  // A5: Cancel CTA pulse loop on unmount.
  React.useEffect(() => () => {
    if (ctaPulseRef.current !== null) {
      clearTimeout(ctaPulseRef.current);
      ctaPulseRef.current = null;
    }
  }, []);

  const cellVisual = React.useCallback((index: number) => {
    const cell = cells[index];
    if (!cell) return null;
    return {
      x: cell.center.x,
      y: cell.center.y,
      width: cell.visual.width,
      height: cell.visual.height,
    };
  }, [cells]);
  const syncTileLayersToRuntimeBoard = React.useCallback((indexes: readonly number[]) => {
    for (const index of indexes) {
      const layerId = `match-tile-${String(index)}`;
      // A1 jitter fix: tween-owned layers (actively dropping) win over JSX home position application.
      // The REFILL_RESYNC_MS second sync pass must not snap position while the drop tween is running.
      if (droppingLayersRef.current.has(layerId)) continue;
      const home = cellVisual(index);
      if (!home || staticIndexSet.has(index) || (runtime.board.cells[index] ?? -1) < 0) {
        setLayerProps(layerId, { visible: false, alpha: 0 });
        continue;
      }
      setLayerProps(layerId, { ...home, visible: true, alpha: 1 });
    }
  }, [cellVisual, runtime.board.cells, setLayerProps, staticIndexSet]);

  // @intent Animate gravity: newly-spawned refill tiles drop in from above the board; settlers (survivors that
  //         moved down) tween from their old cell position to their new one (gravity settle animation).
  // @flow boardBeforeRefill = boardAfterClear (holes mark cleared cells). For each column, survivors are the
  //       tiles that were NOT holes, packed to the bottom. A settler at new index `newIdx` was previously at
  //       `oldIdx` (computed by column scan). New tiles (oldIdx had a hole) spawn from above-board spawnY.
  //       Both paths register in droppingLayersRef (A1 guard) so syncTileLayersToRuntimeBoard skips them.
  //       Settlers use same REFILL_DROP_MS + easeOutQuad + landing squash as spawned tiles for visual parity.
  // @stale 2026-06-11 — gravity settlers now animated (previously skipped, causing teleport-in-place defect).
  const animateRefillDrop = React.useCallback((
    refilledIndexes: readonly number[],
    boardBeforeRefill: readonly number[],
  ) => {
    const cols = payload.level.board.cols;
    const rows = payload.level.board.rows;
    const boardTopY = viewModel.layout.board.y;
    const boardHeight = viewModel.layout.board.height;
    // Uniform spawn point above the board — same Y for every row so all new tiles drop equal distance.
    const spawnY = boardTopY - boardHeight * REFILL_SPAWN_ABOVE_RATIO;

    // Build settler map: newIndex → oldIndex (where the survivor tile came from before collapse).
    // Per column: collect old tile positions (not holes) top-to-bottom = survivors in order.
    // After collapse they pack to the bottom: survivor[0] → row (rows-k), survivor[1] → row (rows-k+1)…
    // where k = survivors.length. Mirror the engine's collapseAndRefillSegments logic.
    const settlerOldIndex = new Map<number, number>();
    for (let c = 0; c < cols; c += 1) {
      const oldSurvivorIndexes: number[] = [];
      for (let r = 0; r < rows; r += 1) {
        const idx = r * cols + c;
        if ((boardBeforeRefill[idx] ?? -1) >= 0) oldSurvivorIndexes.push(idx);
      }
      // After gravity: survivors fill from bottom. missing = rows - oldSurvivorIndexes.length new tiles at top.
      const missing = rows - oldSurvivorIndexes.length;
      for (let i = 0; i < oldSurvivorIndexes.length; i += 1) {
        const newIdx = (missing + i) * cols + c;
        const oldIdx = oldSurvivorIndexes[i] ?? -1;
        if (oldIdx >= 0 && newIdx !== oldIdx) settlerOldIndex.set(newIdx, oldIdx);
      }
    }

    const refilledSet = new Set(refilledIndexes);
    for (const index of refilledSet) {
      if (staticIndexSet.has(index)) continue;
      const home = cellVisual(index);
      if (!home) continue;
      const layerId = `match-tile-${String(index)}`;

      const isNewTile = (boardBeforeRefill[index] ?? -1) < 0;
      const oldIdx = settlerOldIndex.get(index) ?? -1;
      const isSettler = !isNewTile && oldIdx >= 0;

      // Determine start Y: new tiles spawn above board; settlers start at their old cell's Y.
      let startY = spawnY;
      if (isSettler) {
        const oldVisual = cellVisual(oldIdx);
        if (!oldVisual) continue; // can't compute start; skip
        startY = oldVisual.y;
      } else if (!isNewTile) {
        // Tile was there before and didn't move (no-op in column collapse) — no animation needed.
        continue;
      }

      // Snap sprite to start position, then tween to home.
      setLayerProps(layerId, { x: home.x, y: startY, width: home.width, height: home.height, visible: true, alpha: 1 });
      // A1 jitter fix: register layer as tween-owned so syncTileLayersToRuntimeBoard skips it during drop.
      droppingLayersRef.current.add(layerId);
      const handle = createNumericTween({
        id: `native-match3-${layerId}-drop-${String(tweenSeqRef.current += 1)}`,
        target: (nativeTargetsRef.current.get(layerId) ?? {}) as Record<string, number>,
        props: { y: home.y },
        options: { durationMs: REFILL_DROP_MS, easing: 'easeOutQuad' },
        requestFrame,
        cancelFrame,
        now: finitePerformanceNow,
        probeTargetLayerId: layerId,
        onProbeUpdate: (snapshot) => { probeTweenRegistry?.upsert(snapshot); },
      });
      tweenHandlesRef.current.push(handle);
      const capturedHome = home;
      const capturedLayerId = layerId;
      void handle.finished.then(() => {
        // A3(b): landing squash — scaleY 0.92 / scaleX 1.08 → 1.0 over 90ms on touchdown.
        // Use width/height to approximate scale (no standalone scale prop in the tween target).
        // IMPORTANT: setLayerProps must run BEFORE createNumericTween so the tween captures the
        // squashed start values (not the home values) when it reads them from the target on creation.
        setLayerProps(capturedLayerId, {
          width: capturedHome.width * REFILL_SQUASH_SCALE_X,
          height: capturedHome.height * REFILL_SQUASH_SCALE_Y,
        });
        const squashHandle = createNumericTween({
          id: `native-match3-${capturedLayerId}-squash-${String(tweenSeqRef.current += 1)}`,
          target: (nativeTargetsRef.current.get(capturedLayerId) ?? {}) as Record<string, number>,
          props: {
            width: capturedHome.width,
            height: capturedHome.height,
          },
          options: { durationMs: REFILL_SQUASH_MS, easing: 'easeOutQuad' },
          requestFrame,
          cancelFrame,
          now: finitePerformanceNow,
          probeTargetLayerId: capturedLayerId,
          onProbeUpdate: (snapshot) => { probeTweenRegistry?.upsert(snapshot); },
        });
        tweenHandlesRef.current.push(squashHandle);
        void squashHandle.finished.finally(() => {
          tweenHandlesRef.current = tweenHandlesRef.current.filter((current) => current !== squashHandle);
        }).catch(() => undefined);
      }).finally(() => {
        tweenHandlesRef.current = tweenHandlesRef.current.filter((current) => current !== handle);
        // Deregister from dropping registry so the next sync pass can apply home position freely.
        droppingLayersRef.current.delete(capturedLayerId);
      }).catch(() => undefined);
    }
  }, [cellVisual, payload.level.board.cols, payload.level.board.rows, probeTweenRegistry, setLayerProps, staticIndexSet, viewModel.layout.board.height, viewModel.layout.board.y]);

  const flowerVisual = React.useCallback((target: { readonly index: number; readonly stage: number }) => {
    const cell = cells[target.index];
    if (!cell) return null;
    const stageSize = target.stage <= 0
      ? cell.visual.width * 0.68
      : target.stage === 1
        ? cell.visual.width * 0.84
        : cell.visual.width;
    return {
      x: cell.center.x,
      y: cell.center.y - cell.visual.height * 0.08,
      width: stageSize,
      height: stageSize,
    };
  }, [cells]);
  const flowerFlyTarget = React.useCallback(() => {
    const size = Math.max(18, Math.min(34, viewModel.layout.board.cellSize * 0.38));
    return {
      x: viewModel.layout.hud.x + viewModel.layout.hud.width * 0.5,
      y: viewModel.layout.hud.y + viewModel.layout.hud.height * 0.5,
      width: size,
      height: size,
    };
  }, [viewModel.layout.board.cellSize, viewModel.layout.hud]);
  // @intent A3(a): swap tween uses backOut easing for ~6% overshoot-settle on both tiles.
  //         Total 250ms; target positions are exact at completion (backOut reaches progress=1.0 at end).
  //         Must preserve >=2 match-tile tweens with >1px travel (tested).
  // @stale 2026-06-11 — A3: changed easing from easeInOutQuad to backOut for overshoot feel.
  const animateSwap = React.useCallback((fromIndex: number, toIndex: number) => {
    const from = cellVisual(fromIndex);
    const to = cellVisual(toIndex);
    if (!from || !to) return;
    tweenLayer(`match-tile-${String(fromIndex)}`, { x: to.x, y: to.y }, { durationMs: SWAP_MS, easing: 'backOut' });
    tweenLayer(`match-tile-${String(toIndex)}`, { x: from.x, y: from.y }, { durationMs: SWAP_MS, easing: 'backOut' });
  }, [cellVisual, tweenLayer]);
  const animateReturn = React.useCallback((fromIndex: number, toIndex: number) => {
    const from = cellVisual(fromIndex);
    const to = cellVisual(toIndex);
    if (!from || !to) return;
    tweenLayer(`match-tile-${String(fromIndex)}`, { x: from.x, y: from.y }, { durationMs: SWAP_MS, easing: 'easeInOutQuad' });
    tweenLayer(`match-tile-${String(toIndex)}`, { x: to.x, y: to.y }, { durationMs: SWAP_MS, easing: 'easeInOutQuad' });
  }, [cellVisual, tweenLayer]);
  // @intent Animate a short deny-shake on a tile when the drag target is non-swappable (Fix 4).
  // @flow Called when toIndex is NOT swappable — plays 3× ±SHAKE_OFFSET_PX x-nudge over ~120ms on the dragged
  //       tile only; no swap tween, no engine trySwapIndexes call. Keeps full swap+revert for valid-but-no-match
  //       swaps (genre-standard: player needs feedback that the swap happened but found no match).
  // @stale 2026-06-10 — added to fix deny-shake defect: renderer was playing full swap+revert for static targets.
  const animateDenyShake = React.useCallback((fromIndex: number) => {
    const home = cellVisual(fromIndex);
    if (!home) return;
    const layerId = `match-tile-${String(fromIndex)}`;
    // Step 0: snap to home (no prior tween running for this gesture).
    setLayerProps(layerId, { x: home.x, y: home.y });
    // Three shake steps: right → left → right → settle back.
    const offsets = [SHAKE_OFFSET_PX, -SHAKE_OFFSET_PX, SHAKE_OFFSET_PX, 0];
    for (let i = 0; i < offsets.length; i += 1) {
      const offset = offsets[i] ?? 0;
      const delayMs = SHAKE_STEP_MS * i;
      scheduleResolutionStep(delayMs, () => {
        tweenLayer(layerId, { x: home.x + offset }, { durationMs: SHAKE_STEP_MS, easing: 'easeInOutQuad' });
      });
    }
    // After the shake completes, fully sync back to home position.
    scheduleResolutionStep(SHAKE_STEP_MS * offsets.length + 8, () => {
      setLayerProps(layerId, { x: home.x, y: home.y });
    });
  }, [cellVisual, scheduleResolutionStep, setLayerProps, tweenLayer]);

  // @intent A4: Show cascade combo popup label (depth >= 2) centered over board: scale-in → hold → fade.
  // @flow depth -> label from COMBO_POPUP_LABELS; mounts NativeText via setComboPopup state; pending-tween
  //       drain in registerNativeTarget starts scale 0→1 on mount; scheduleResolutionStep chains hold → fade;
  //       on fade complete: setComboPopup(null).
  // @stale 2026-06-11 — A4: cascade combo popup animation.
  const animateCascadeCombo = React.useCallback((depth: number) => {
    if (depth < 2) return;
    const label = COMBO_POPUP_LABELS[Math.min(depth, COMBO_POPUP_LABELS.length - 1)] ?? 'Amazing!';
    const popupId = `combo-popup-${String(Date.now())}`;
    setComboPopup({ id: popupId, label });
    const boardCX = viewModel.layout.board.x + viewModel.layout.board.width / 2;
    const boardCY = viewModel.layout.board.y + viewModel.layout.board.height / 2;
    pendingComboPopupTweensRef.current.set(popupId, () => {
      const target = nativeTargetsRef.current.get(popupId);
      if (!target) return;
      // Snap to scale 0, centered, full alpha.
      (target as Record<string, number>).scale = 0;
      (target as Record<string, number>).x = boardCX;
      (target as Record<string, number>).y = boardCY;
      (target as Record<string, number>).alpha = 1;
      // Scale in 0 → 1.
      tweenLayer(popupId, { scale: 1 }, { durationMs: COMBO_POPUP_SCALE_IN_MS, easing: 'backOut' });
      // Hold then fade.
      scheduleResolutionStep(COMBO_POPUP_SCALE_IN_MS + COMBO_POPUP_HOLD_MS, () => {
        tweenLayer(popupId, { alpha: 0 }, { durationMs: COMBO_POPUP_FADE_MS, easing: 'easeInQuad' });
        scheduleResolutionStep(COMBO_POPUP_FADE_MS, () => {
          setComboPopup(null);
        });
      });
    });
  }, [scheduleResolutionStep, tweenLayer, viewModel.layout.board]);

  // @intent A5: Start looping CTA pulse (scale 1 → 1.06 → 1) after win; cancelable via ctaPulseRef.
  // @flow Recursive setTimeout chain: tween up → schedule tween down → schedule next pulse.
  //       Stopped by clearing ctaPulseRef.current on unmount or before next win trigger.
  // @stale 2026-06-11 — A5: win CTA pulse loop.
  const startCtaPulse = React.useCallback(() => {
    const pulse = () => {
      tweenLayer('native-match3-cta', { scale: 1.06 }, { durationMs: CTA_PULSE_HALF_MS, easing: 'easeInOutQuad' });
      ctaPulseRef.current = setTimeout(() => {
        tweenLayer('native-match3-cta', { scale: 1.0 }, { durationMs: CTA_PULSE_HALF_MS, easing: 'easeInOutQuad' });
        ctaPulseRef.current = setTimeout(pulse, CTA_PULSE_HALF_MS);
      }, CTA_PULSE_HALF_MS);
    };
    pulse();
  }, [tweenLayer]);

  // @intent A5: Populate confetti layout ref once on win and show confetti graphic for 1200ms.
  // @flow Reads palette from skin; places CONFETTI_COUNT rects randomly across board+endcard area;
  //       sets confettiVisible=true; schedules setConfettiVisible(false) after 1200ms.
  // @stale 2026-06-11 — A5: win confetti burst.
  const animateWinCelebration = React.useCallback(() => {
    const palette = payload.skin.styleBible.palette;
    const boardX = viewModel.layout.board.x;
    const boardY = viewModel.layout.board.y;
    const boardW = viewModel.layout.board.width;
    const boardH = viewModel.layout.board.height;
    const pieces: Array<{ x: number; y: number; w: number; h: number; color: number }> = [];
    for (let i = 0; i < CONFETTI_COUNT; i++) {
      const colorHex = palette[i % palette.length] ?? '#ffffff';
      const color = parseInt(colorHex.replace('#', ''), 16);
      const x = boardX + Math.random() * boardW;
      const y = boardY + Math.random() * boardH;
      const w = 6 + Math.random() * 8;
      const h = w * 0.5;
      pieces.push({ x, y, w, h, color: isNaN(color) ? 0xffffff : color });
    }
    confettiLayoutRef.current = pieces;
    setConfettiVisible(true);
    scheduleResolutionStep(1200, () => { setConfettiVisible(false); });
  }, [payload.skin.styleBible.palette, scheduleResolutionStep, viewModel.layout.board]);

  // @intent Animate a tile clear: pop+scale, then fade out. Also spawns A2 clear-fx particles.
  // @flow index + assetUrl -> pop tween on tile layer; 5-7 mini-sprite particles appended to clearFxSprites
  //       state (pending-tween mount-hook pattern); each particle tweens outward + alpha→0 over CLEAR_FX_MS;
  //       on completion each particle filters itself from clearFxSprites. Capped at MAX_CLEAR_FX_SPRITES total.
  // @stale 2026-06-11 — A2: assetUrl param added; clear-fx particle spawning added.
  const animateTileClear = React.useCallback((index: number, assetUrl: string) => {
    const home = cellVisual(index);
    if (!home) return;
    tweenLayer(`match-tile-${String(index)}`, {
      x: home.x,
      y: home.y,
      width: home.width * 1.12,
      height: home.height * 1.12,
      alpha: 1,
    }, { durationMs: CLEAR_POP_MS, easing: 'backOut' });
    scheduleResolutionStep(CLEAR_POP_MS, () => {
      tweenLayer(`match-tile-${String(index)}`, {
        x: home.x,
        y: home.y,
        width: home.width * 0.24,
        height: home.height * 0.24,
        alpha: 0,
      }, { durationMs: CLEAR_FADE_MS, easing: 'easeOutQuad' });
    });

    // A2: Spawn clear-fx particles — 5-7 mini-copies of the tile texture radiating from cell center.
    // Stale-closure guard: read clearFxSprites.length only as a cheap early-exit heuristic; the real cap
    // enforcement happens INSIDE the functional setClearFxSprites update which always sees fresh state.
    if (clearFxSprites.length >= MAX_CLEAR_FX_SPRITES) return;

    const fxCount = CLEAR_FX_COUNT_MIN + Math.floor(Math.random() * (CLEAR_FX_COUNT_MAX - CLEAR_FX_COUNT_MIN + 1));
    const fxSize = home.width * CLEAR_FX_SCALE;
    const newFxSprites: Array<{ layerId: string; assetUrl: string; x: number; y: number; width: number; height: number }> = [];
    // Increment the module-level batch counter so repeated clears of the same cell index get unique layer ids.
    const fxBatch = (clearFxBatchCounter += 1);

    for (let n = 0; n < fxCount; n += 1) {
      const layerId = `match-clear-fx-${String(index)}-b${String(fxBatch)}-${String(n)}`;
      // Random angle (full circle) and travel distance.
      const angle = (n / fxCount) * Math.PI * 2 + Math.random() * (Math.PI / fxCount);
      const travel = home.width * CLEAR_FX_TRAVEL * (0.6 + Math.random() * 0.4);
      const targetX = home.x + Math.cos(angle) * travel;
      // Slight downward gravity bias on the y target.
      const targetY = home.y + Math.sin(angle) * travel + home.height * CLEAR_FX_GRAVITY;
      newFxSprites.push({ layerId, assetUrl, x: home.x, y: home.y, width: fxSize, height: fxSize });

      const capturedLayerId = layerId;
      const capturedTargetX = targetX;
      const capturedTargetY = targetY;
      pendingClearFxTweensRef.current.set(capturedLayerId, () => {
        setLayerProps(capturedLayerId, { x: home.x, y: home.y, width: fxSize, height: fxSize, visible: true, alpha: 1 });
        const handle = createNumericTween({
          id: `native-match3-${capturedLayerId}-${String(tweenSeqRef.current += 1)}`,
          target: (nativeTargetsRef.current.get(capturedLayerId) ?? {}) as Record<string, number>,
          props: { x: capturedTargetX, y: capturedTargetY, alpha: 0 },
          options: { durationMs: CLEAR_FX_MS, easing: 'easeOutQuad' },
          requestFrame,
          cancelFrame,
          now: finitePerformanceNow,
          probeTargetLayerId: capturedLayerId,
          onProbeUpdate: (snapshot) => { probeTweenRegistry?.upsert(snapshot); },
        });
        tweenHandlesRef.current.push(handle);
        void handle.finished.finally(() => {
          tweenHandlesRef.current = tweenHandlesRef.current.filter((current) => current !== handle);
          // Remove this particle sprite from state on completion.
          setClearFxSprites((prev) => prev.filter((s) => s.layerId !== capturedLayerId));
        }).catch(() => undefined);
      });
    }

    // Append new particles to state — functional update so concurrent batches don't overwrite each other.
    // Cap computation is INSIDE the callback so it uses fresh (not stale-closure) state length.
    setClearFxSprites((prev) => {
      const available = MAX_CLEAR_FX_SPRITES - prev.length;
      if (available <= 0) return prev;
      return [...prev, ...newFxSprites.slice(0, available)];
    });
  }, [cellVisual, clearFxSprites.length, probeTweenRegistry, scheduleResolutionStep, setLayerProps, tweenLayer]);

  // Б1: Animate a special detonation blast for `clearedIndexes` with the given detonation kind.
  // @intent Rocket → horizontal/vertical line-sweep flash rect (~250ms); bomb → radial ring per cleared cell;
  //         colorBomb → reuse clear particles on each cleared cell (no extra layer needed).
  //         All blast-fx layers use id prefix `match-blast-fx-` so the evidence classifier marks them 'effect'.
  // @flow Called from resolveMatched when detonatedSpecials is non-empty; detonation kind drives visual branch.
  // @stale 2026-06-11 — Б1: special detonation blast animation.
  const animateSpecialBlast = React.useCallback((
    detonatedIndex: number,
    kind: string,
    clearedIndexes: readonly number[],
    clearAssetUrls: ReadonlyMap<number, string>,
  ) => {
    const home = cellVisual(detonatedIndex);
    if (!home) return;
    const batch = (blastFxBatchCounter += 1);

    if (kind === 'rocket' || kind === 'rocketH' || kind === 'rocketV') {
      // Line-sweep: a wide flash rect that covers the full row (H) or column (V) of the board.
      const isV = kind === 'rocketV';
      const board = viewModel.layout.board;
      const sweepW = isV ? home.width * 1.1 : board.width;
      const sweepH = isV ? board.height : home.height * 1.1;
      const sweepX = isV ? home.x : board.x + board.width / 2;
      const sweepY = isV ? board.y + board.height / 2 : home.y;
      const layerId = `match-blast-fx-${String(detonatedIndex)}-b${String(batch)}-sweep`;
      setBlastFxSprites((prev) => [
        ...prev,
        { layerId, kind: isV ? 'sweep-v' : 'sweep-h', x: sweepX, y: sweepY, width: sweepW, height: sweepH, color: 0xffffff },
      ]);
      const capturedLayerId = layerId;
      pendingBlastFxTweensRef.current.set(capturedLayerId, () => {
        setLayerProps(capturedLayerId, { alpha: 0.85, visible: true });
        const handle = createNumericTween({
          id: `native-match3-${capturedLayerId}-${String(tweenSeqRef.current += 1)}`,
          target: (nativeTargetsRef.current.get(capturedLayerId) ?? {}) as Record<string, number>,
          props: { alpha: 0 },
          options: { durationMs: BLAST_SWEEP_MS, easing: 'easeOutQuad' },
          requestFrame,
          cancelFrame,
          now: finitePerformanceNow,
          probeTargetLayerId: capturedLayerId,
          onProbeUpdate: (snapshot) => { probeTweenRegistry?.upsert(snapshot); },
        });
        tweenHandlesRef.current.push(handle);
        void handle.finished.finally(() => {
          tweenHandlesRef.current = tweenHandlesRef.current.filter((c) => c !== handle);
          setBlastFxSprites((prev) => prev.filter((s) => s.layerId !== capturedLayerId));
        }).catch(() => undefined);
      });
    } else if (kind === 'bomb') {
      // Radial ring: single flash rect centered on the bomb, fades out.
      const ringSize = home.width * 3.2;
      const layerId = `match-blast-fx-${String(detonatedIndex)}-b${String(batch)}-ring`;
      setBlastFxSprites((prev) => [
        ...prev,
        { layerId, kind: 'ring', x: home.x, y: home.y, width: ringSize, height: ringSize, color: 0xff8800 },
      ]);
      const capturedLayerId = layerId;
      pendingBlastFxTweensRef.current.set(capturedLayerId, () => {
        setLayerProps(capturedLayerId, { alpha: 0.7, visible: true, width: home.width * 0.5, height: home.width * 0.5 });
        const handle = createNumericTween({
          id: `native-match3-${capturedLayerId}-${String(tweenSeqRef.current += 1)}`,
          target: (nativeTargetsRef.current.get(capturedLayerId) ?? {}) as Record<string, number>,
          props: { alpha: 0, width: ringSize, height: ringSize },
          options: { durationMs: BLAST_SWEEP_MS, easing: 'easeOutQuad' },
          requestFrame,
          cancelFrame,
          now: finitePerformanceNow,
          probeTargetLayerId: capturedLayerId,
          onProbeUpdate: (snapshot) => { probeTweenRegistry?.upsert(snapshot); },
        });
        tweenHandlesRef.current.push(handle);
        void handle.finished.finally(() => {
          tweenHandlesRef.current = tweenHandlesRef.current.filter((c) => c !== handle);
          setBlastFxSprites((prev) => prev.filter((s) => s.layerId !== capturedLayerId));
        }).catch(() => undefined);
      });
    }
    // colorBomb: reuse clear particles on each cleared cell — no separate blast layer needed.
    for (const idx of clearedIndexes) {
      animateTileClear(idx, clearAssetUrls.get(idx) ?? '');
    }
    clearPop(3); // detonation pitch: depth+2 above base cascade depth 1.
  }, [animateTileClear, cellVisual, probeTweenRegistry, setLayerProps, tweenLayer, viewModel.layout.board]);

  const changedFlowerTargets = React.useCallback((
    beforeTargets: ReturnType<typeof runtime.objectiveTargets>,
    afterTargets: ReturnType<typeof runtime.objectiveTargets>,
  ) => {
    const beforeByIndex = new Map(beforeTargets.map((target) => [target.index, target]));
    return afterTargets
      .map((after) => ({ before: beforeByIndex.get(after.index), after }))
      .filter((entry) => entry.before && (entry.after.stage !== entry.before.stage || entry.after.collected !== entry.before.collected));
  }, [runtime]);
  // @intent Animate stage-advanced and collected flowers flying toward the HUD objective anchor (Defect C fix).
  // @flow Each changed flower: mount a dedicated flight-sprite JSX layer (match-static-fly-{index}) via
  //       setFlightSprites; tween that layer to flyTarget (alpha→0) independently of the home sprite so React
  //       re-renders of the home sprite (x/y/width/height prop updates) cannot interrupt the flight path.
  //       On FLOWER_FLY_MS completion: unmount the flight sprite via setFlightSprites, restore home sprite
  //       state via setLayerProps, and trigger a revision re-render.
  // @stale 2026-06-10 — flightSprites state is cleared at completion; one batch per move.
  const animateFlowerChanges = React.useCallback((changes: ReturnType<typeof changedFlowerTargets>) => {
    const flyingIndexes = new Set<number>();
    const flyTarget = flowerFlyTarget();
    const flights: Match3ProbeObjectiveFlightV1[] = [];
    const newFlightSprites: Array<{ index: number; assetUrl: string; x: number; y: number; width: number; height: number }> = [];
    for (const change of changes) {
      if (!change.before) continue;
      const beforeHome = flowerVisual(change.before);
      const afterHome = flowerVisual(change.after);
      if (!beforeHome || !afterHome) continue;
      const stageIndex = Math.max(0, Math.min(payload.skin.slots.objectiveStages.length - 1, Math.trunc(change.before.stage)));
      const assetUrl = payload.skin.slots.objectiveStages[stageIndex] ?? payload.skin.slots.objectiveStages[0] ?? '';
      const coord = runtime.coordAt(change.after.index);
      const flight: Match3ProbeObjectiveFlightV1 = {
        sourceIndex: change.after.index,
        row: coord.row,
        col: coord.col,
        source: beforeHome,
        target: flyTarget,
        durationMs: FLOWER_FLY_MS,
        status: 'running',
      };
      flights.push(flight);
      flyingIndexes.add(change.after.index);
      // Register a dedicated flight-sprite layer that will be mounted by React and tweened independently.
      // Hide the home sprite imperatively while the flight sprite carries the animation so both don't show
      // simultaneously (Defect C fix: home sprite React prop-updates cannot snap the flight path).
      const homeLayerId = `match-static-${String(change.after.index)}`;
      setLayerProps(homeLayerId, { alpha: 0 });
      newFlightSprites.push({ index: change.after.index, assetUrl, ...beforeHome });
      scheduleResolutionStep(FLOWER_FLY_MS, () => {
        // Unmount the flight sprite and mark flight complete; restore home sprite to collected/stage state.
        collectChime(); // A6: chime SFX when collect flight lands.
        setFlightSprites((current) => current.filter((s) => s.index !== change.after.index));
        updateMeta({
          objectiveFlights: metaRef.current.objectiveFlights.map((current) => (
            current.sourceIndex === flight.sourceIndex ? { ...current, status: 'finished' } : current
          )),
        });
        const homeLayerId = `match-static-${String(change.after.index)}`;
        if (change.after.collected) {
          // Restore to a dim collected-state decoration rather than hiding (Defect A visual fix).
          const collectedHome = flowerVisual({ index: change.after.index, stage: change.after.maxStage });
          setLayerProps(homeLayerId, { ...(collectedHome ?? afterHome), visible: true, alpha: COLLECTED_FLOWER_ALPHA });
        } else {
          setLayerProps(homeLayerId, { ...afterHome, visible: true, alpha: 1 });
          // A3(d): flower stage-growth pop — scale 1→1.3→1 on stage advance (not final collect).
          tweenLayer(homeLayerId, { scale: FLOWER_GROWTH_PEAK }, { durationMs: FLOWER_GROWTH_HALF_MS, easing: 'easeOutQuad' });
          scheduleResolutionStep(FLOWER_GROWTH_HALF_MS, () => {
            tweenLayer(homeLayerId, { scale: 1 }, { durationMs: FLOWER_GROWTH_HALF_MS, easing: 'easeInQuad' });
          });
        }
        // A3(c): HUD counter bounce — scale 1→1.25→1 over 160ms when collect flight lands.
        const counterLayerId = 'native-match3-hud-counter';
        tweenLayer(counterLayerId, { scale: HUD_BOUNCE_PEAK }, { durationMs: HUD_BOUNCE_HALF_MS, easing: 'easeOutQuad' });
        scheduleResolutionStep(HUD_BOUNCE_HALF_MS, () => {
          tweenLayer(counterLayerId, { scale: 1 }, { durationMs: HUD_BOUNCE_HALF_MS, easing: 'easeInQuad' });
        });
        setRevision((current) => current + 1);
      });
    }
    if (newFlightSprites.length > 0) {
      // Register deterministic mount callbacks BEFORE setFlightSprites so registerNativeTarget can drain them
      // synchronously as soon as React commits each flight sprite — no RAF timing guess needed.
      for (const fs of newFlightSprites) {
        const flyLayerId = `match-static-fly-${String(fs.index)}`;
        // Capture loop variables in the callback closure.
        const capturedFs = fs;
        const capturedFlyTarget = flyTarget;
        pendingFlightTweensRef.current.set(flyLayerId, () => {
          setLayerProps(flyLayerId, { x: capturedFs.x, y: capturedFs.y, width: capturedFs.width, height: capturedFs.height, visible: true, alpha: 1 });
          tweenLayer(flyLayerId, {
            x: capturedFlyTarget.x,
            y: capturedFlyTarget.y,
            width: capturedFlyTarget.width,
            height: capturedFlyTarget.height,
            alpha: 0,
          }, { durationMs: FLOWER_FLY_MS, easing: 'easeInOutQuad' });
        });
      }
      // Append to any existing in-flight batch so concurrent cascades don't unmount each other's sprites.
      setFlightSprites((prev) => [...prev, ...newFlightSprites]);
    }
    if (flights.length > 0) updateMeta({ objectiveFlights: flights });
    return flyingIndexes;
  }, [changedFlowerTargets, flowerFlyTarget, flowerVisual, payload.skin.slots.objectiveStages, runtime, scheduleResolutionStep, setLayerProps, tweenLayer, updateMeta]);

  const buildProbeState = React.useCallback((): Match3ProbeStateV1 => {
    const staticCellIndexes = runtime.staticCellIndexes();
    const staticCellSet = new Set(staticCellIndexes);
    const nextMove = tupleMove(runtime.nextMoveIndexes());
    return {
      rows: payload.level.board.rows,
      cols: payload.level.board.cols,
      board: runtime.board.cells.slice(),
      ...(payload.level.script?.firstMove === undefined
        ? {}
        : {
            firstMove: {
              from: [payload.level.script.firstMove.a.row, payload.level.script.firstMove.a.col] as const,
              to: [payload.level.script.firstMove.b.row, payload.level.script.firstMove.b.col] as const,
            },
          }),
      ...(nextMove === null ? {} : { nextMove }),
      cellRects: cells.map((cell) => ({
        tileIndex: cell.tileIndex,
        row: cell.row,
        col: cell.col,
        x: cell.x,
        y: cell.y,
        width: cell.width,
        height: cell.height,
      })),
      tileLayerIds: cells
        .filter((cell) => !staticCellSet.has(cell.tileIndex) && (runtime.board.cells[cell.tileIndex] ?? -1) >= 0)
        .map((cell) => `match-tile-${String(cell.tileIndex)}`),
      ...(metaRef.current.lastAcceptedMove === null ? {} : { lastAcceptedMove: metaRef.current.lastAcceptedMove }),
      acceptedMoveSeq: metaRef.current.acceptedMoveSeq,
      ...(metaRef.current.lastRejectedMove === null ? {} : { lastRejectedMove: metaRef.current.lastRejectedMove }),
      rejectedMoveSeq: metaRef.current.rejectedMoveSeq,
      lastRepaintIndexes: metaRef.current.lastRepaintIndexes,
      ...(metaRef.current.lastResolutionPlan === null ? {} : { lastResolutionPlan: metaRef.current.lastResolutionPlan }),
      staticCellIndexes,
      ...(tutorialMaskIndexes.length === 0 ? {} : { tutorialMaskIndexes }),
      ...(metaRef.current.objectiveFlights.length === 0 ? {} : { objectiveFlights: metaRef.current.objectiveFlights }),
      resolutionPhase: metaRef.current.resolutionPhase,
      flowerStageProgress: runtime.objectiveStageProgress(),
      objectiveRemaining: runtime.objectiveRemaining(),
      tileCount: runtime.tileCount(),
      resolving: metaRef.current.resolving,
      ctaShown: metaRef.current.ended,
      // Б1: expose current specials board state (additive; absent if map is empty).
      ...(specialsMapRef.current.size === 0 ? {} : {
        specials: Array.from(specialsMapRef.current.entries()).map(([index, kind]): Match3ProbeSpecialEntryV1 => ({ index, kind })),
      }),
    };
  }, [cells, payload.level.board.cols, payload.level.board.rows, payload.level.script?.firstMove, runtime, revision, tupleMove, tutorialMaskIndexes]);

  React.useEffect(() => {
    probeStateProviders.set('match3', buildProbeState);
    return () => {
      if (probeStateProviders.get('match3') === buildProbeState) probeStateProviders.delete('match3');
    };
  }, [buildProbeState, probeStateProviders]);

  React.useEffect(() => {
    const globalRef = globalThis as { __match3?: NativeMatch3Hooks };
    const previous = globalRef.__match3;
    const hooks: NativeMatch3Hooks = {
      firstMoveDesign: () => designMove(runtime.firstMoveIndexes()),
      nextValidSwapDesign: () => designMove(runtime.nextMoveIndexes()),
      boardTileCount: () => runtime.tileCount(),
      objectiveRemaining: () => runtime.objectiveRemaining(),
      ctaShown: () => metaRef.current.ended,
    };
    globalRef.__match3 = hooks;
    return () => {
      if (globalRef.__match3 !== hooks) return;
      if (previous === undefined) delete globalRef.__match3;
      else globalRef.__match3 = previous;
    };
  }, [designMove, runtime]);

  React.useEffect(() => {
    emit(match3TelemetryEvents.ready, asJson({ topologyId: payload.level.topologyId }));
    emit(match3TelemetryEvents.tutorialVisible, asJson({ topologyId: payload.level.topologyId }));
  }, [emit, payload.level.topologyId]);

  React.useEffect(() => {
    const unregisterDown = onInput('pointerdown', (event) => {
      // A6: lazy AudioContext init on first user gesture (autoplay policy).
      initSfxOnGesture();
      dragRef.current = beginMatch3Drag({
        point: { x: event.x, y: event.y },
        cells,
        isSwappableIndex: (index) => runtime.isSwappableIndex(index),
      });
    });
    const unregisterUp = onInput('pointerup', (event) => {
      // A6: lazy AudioContext init on first user gesture (autoplay policy).
      initSfxOnGesture();
      const move = finishMatch3Drag({
        drag: dragRef.current,
        point: { x: event.x, y: event.y },
        rows: payload.level.board.rows,
        cols: payload.level.board.cols,
      });
      dragRef.current = null;
      if (!move || metaRef.current.ended || metaRef.current.resolving) return;
      // Fix 4: if the drag TARGET is a non-swappable cell (static flower host, blocked index),
      // deny the gesture with a short shake on the dragged tile only — no swap tween, no engine call.
      // Full swap+revert is reserved for valid-but-no-match swaps (genre standard).
      if (!runtime.isSwappableIndex(move.toIndex)) {
        denyBuzz(); // A6: buzz for static-target deny.
        animateDenyShake(move.fromIndex);
        return;
      }
      swapBlip(); // A6: blip on swap attempt (accepted or rejected).
      const beforeBoard = runtime.board.cells.slice();
      const accepted = runtime.trySwapIndexes(move.fromIndex, move.toIndex);
      setVisualBoard(beforeBoard);
      animateSwap(move.fromIndex, move.toIndex);
      if (!accepted.ok || accepted.matchedIndexes.length === 0) {
        const nextRejectedMoveSeq = metaRef.current.rejectedMoveSeq + 1;
        const rejectedBoard = runtime.board.cells.slice();
        updateMeta({
          resolving: true,
          resolutionPhase: 'swap',
          rejectedMoveSeq: nextRejectedMoveSeq,
          lastRejectedMove: { fromIndex: move.fromIndex, toIndex: move.toIndex },
          lastRepaintIndexes: [move.fromIndex, move.toIndex],
          objectiveFlights: [],
          lastResolutionPlan: {
            result: 'rejected',
            fromIndex: move.fromIndex,
            toIndex: move.toIndex,
            acceptedMove: null,
            rejectedMove: { fromIndex: move.fromIndex, toIndex: move.toIndex },
            initialMatchedIndexes: [],
            postSwapBoard: rejectedBoard,
            cascades: [],
            repaintIndexes: [move.fromIndex, move.toIndex],
            finalBoard: rejectedBoard,
            outcome: 'none',
          },
        });
        emit(match3TelemetryEvents.meaningfulStateChange, asJson({
          topologyId: payload.level.topologyId,
          action: 'reverted_no_match',
          fromIndex: move.fromIndex,
          toIndex: move.toIndex,
          objectiveRemaining: runtime.objectiveRemaining(),
        }));
        scheduleResolutionStep(SWAP_MS, () => {
          animateReturn(move.fromIndex, move.toIndex);
        });
        scheduleResolutionStep(SWAP_MS * 2, () => {
          setVisualBoard(runtime.board.cells.slice());
          syncTileLayersToRuntimeBoard([move.fromIndex, move.toIndex]);
          updateMeta({
            resolving: false,
            resolutionPhase: 'idle',
            lastRepaintIndexes: [move.fromIndex, move.toIndex],
          }, { render: true });
        });
        return;
      }
      const nextMoveSeq = metaRef.current.acceptedMoveSeq + 1;
      updateMeta({
        resolving: true,
        resolutionPhase: 'swap',
        acceptedMoveSeq: nextMoveSeq,
        lastAcceptedMove: { fromIndex: move.fromIndex, toIndex: move.toIndex },
        lastRepaintIndexes: [move.fromIndex, move.toIndex],
        objectiveFlights: [],
        lastResolutionPlan: {
          result: 'accepted',
          fromIndex: move.fromIndex,
          toIndex: move.toIndex,
          acceptedMove: { fromIndex: move.fromIndex, toIndex: move.toIndex },
          rejectedMove: null,
          initialMatchedIndexes: [...accepted.matchedIndexes],
          postSwapBoard: runtime.board.cells.slice(),
          cascades: [],
          repaintIndexes: [move.fromIndex, move.toIndex],
          finalBoard: runtime.board.cells.slice(),
          outcome: 'none',
        },
      });
      if (nextMoveSeq === 1) {
        emit(match3TelemetryEvents.firstInteraction, asJson({ topologyId: payload.level.topologyId, gesture: 'drag' }));
        setHadFirstInteraction(true);
      }
      const resolveMatched = (matchedIndexes: readonly number[], cascadeDepth = 1) => {
        if (matchedIndexes.length === 0) {
          const end = runtime.completeMove();
          const ended = end.outcome === 'win';
          const previousPlan = metaRef.current.lastResolutionPlan;
          updateMeta({
            ended,
            resolving: false,
            resolutionPhase: 'complete',
            lastResolutionPlan: previousPlan && previousPlan.result === 'accepted'
              ? { ...previousPlan, finalBoard: runtime.board.cells.slice(), outcome: end.outcome }
              : previousPlan,
          }, { render: true });
          if (ended) {
            emit(match3TelemetryEvents.ctaShown, asJson({ topologyId: payload.level.topologyId }));
            emit(match3TelemetryEvents.endcardVisible, asJson({ topologyId: payload.level.topologyId }));
            // A5: Win celebration — confetti burst + CTA pulse loop.
            animateWinCelebration();
            startCtaPulse();
            winSting(); // A6: win sting SFX.
          }
          return;
        }
        // A4: Show cascade combo popup for depth >= 2 cascades.
        animateCascadeCombo(cascadeDepth);
        const flowersBefore = runtime.objectiveTargets();
        // A2: capture tile asset urls before clearIndexes so particles use the correct texture.
        const clearAssetUrls = new Map(matchedIndexes.map((index) => {
          const tileType = runtime.board.cells[index] ?? -1;
          const tileId = tileType >= 0 ? runtime.tileIdForType(tileType) : null;
          const url = tileId != null ? (tileAssetById.get(tileId) ?? payload.skin.slots.tileTypes[0] ?? '') : '';
          return [index, url] as const;
        }));
        const cleared = runtime.clearIndexes(matchedIndexes);
        const boardAfterClear = runtime.board.cells.slice();
        const progress = runtime.recordClear(cleared, matchedIndexes);
        // Б1: read spawned/detonated specials from the resolution step if the engine provided them.
        const currentPlan = metaRef.current.lastResolutionPlan;
        const latestCascade = currentPlan?.cascades.at(-1);
        const spawnedSpecials = latestCascade?.spawnedSpecials ?? [];
        const detonatedSpecials = latestCascade?.detonatedSpecials ?? [];
        // Update specials map: remove detonated, add spawned.
        for (const det of detonatedSpecials) specialsMapRef.current.delete(det.index);
        for (const sp of spawnedSpecials) specialsMapRef.current.set(sp.index, sp.kind);
        // Emit telemetry for spawned/detonated specials.
        if (spawnedSpecials.length > 0) {
          emit(match3TelemetryEvents.specialSpawned, asJson({
            topologyId: payload.level.topologyId,
            specials: spawnedSpecials.map((s) => ({ index: s.index, kind: s.kind })),
          }));
        }
        if (detonatedSpecials.length > 0) {
          emit(match3TelemetryEvents.specialDetonated, asJson({
            topologyId: payload.level.topologyId,
            specials: detonatedSpecials.map((s) => ({ index: s.index, kind: s.kind })),
          }));
        }
        const flowerChanges = changedFlowerTargets(flowersBefore, runtime.objectiveTargets());
        const flyingFlowers = animateFlowerChanges(flowerChanges);
        // Б1: animate blast effects for detonated specials; for non-detonation clears use normal clear animation.
        const detonatedSet = new Set(detonatedSpecials.map((s) => s.index));
        if (detonatedSpecials.length > 0) {
          for (const det of detonatedSpecials) {
            animateSpecialBlast(det.index, det.kind, matchedIndexes, clearAssetUrls);
          }
          // Still animate clear pop + tile shrink for non-detonated cells in the blast radius.
          for (const index of matchedIndexes) {
            if (!detonatedSet.has(index)) animateTileClear(index, clearAssetUrls.get(index) ?? '');
          }
        } else {
          for (const index of matchedIndexes) animateTileClear(index, clearAssetUrls.get(index) ?? '');
        }
        clearPop(cascadeDepth); // A6: pop SFX with pitch rising with cascade depth.
        updateMeta({
          resolutionPhase: 'clear',
          lastRepaintIndexes: [...matchedIndexes],
        });
        emit(match3TelemetryEvents.meaningfulStateChange, asJson({
          topologyId: payload.level.topologyId,
          cleared,
          objectiveRemaining: progress.objectiveRemaining,
        }));
        emit(match3TelemetryEvents.objectiveProgress, asJson({
          topologyId: payload.level.topologyId,
          objectiveRemaining: progress.objectiveRemaining,
        }));
        scheduleResolutionStep(CLEAR_POP_MS + CLEAR_FADE_MS, () => {
          // Capture board state before collapse+refill so animateRefillDrop can detect newly-spawned tiles.
          const boardBeforeRefill = boardAfterClear;
          const refill = runtime.collapseAndRefill(matchedIndexes);
          const changed = [...new Set([move.fromIndex, move.toIndex, ...matchedIndexes, ...refill.changedIndexes])];
          const boardAfterRefill = runtime.board.cells.slice();
          const previousPlan = metaRef.current.lastResolutionPlan;
          const nextCascade = {
            phase: 'clear-refill' as const,
            matchedIndexes: [...matchedIndexes],
            boardAfterClear,
            cleared,
            progress,
            refillChangedIndexes: [...refill.changedIndexes],
            boardAfterRefill,
            repaintIndexes: [...new Set([...matchedIndexes, ...refill.changedIndexes])],
          };
          updateMeta({
            resolutionPhase: 'refill',
            lastRepaintIndexes: changed,
            lastResolutionPlan: previousPlan && previousPlan.result === 'accepted'
              ? {
                  ...previousPlan,
                  cascades: [...previousPlan.cascades, nextCascade],
                  repaintIndexes: [...new Set([...previousPlan.repaintIndexes, ...changed])],
                  finalBoard: boardAfterRefill,
                }
              : previousPlan,
          });
          emit(match3TelemetryEvents.matchResolved, asJson({
            topologyId: payload.level.topologyId,
            changed: changed.length,
          }));
          emit(match3TelemetryEvents.refillComplete, asJson({
            topologyId: payload.level.topologyId,
            tileCount: runtime.tileCount(),
          }));
          const showRefillDelay = flyingFlowers.size > 0 ? Math.max(0, FLOWER_FLY_MS - CLEAR_POP_MS - CLEAR_FADE_MS) : 0;
          // Sync board visuals then apply drop-in animation for newly-spawned tiles (Defect B fix).
          // animateRefillDrop is called only on the FIRST sync to avoid duplicate tween starts.
          const syncRefilledBoard = (withDropAnimation: boolean) => {
            setVisualBoard(runtime.board.cells.slice());
            syncTileLayersToRuntimeBoard(allCellIndexes);
            if (withDropAnimation) animateRefillDrop(refill.changedIndexes, boardBeforeRefill);
          };
          scheduleResolutionStep(showRefillDelay, () => { syncRefilledBoard(true); });
          scheduleResolutionStep(showRefillDelay + Math.min(REFILL_RESYNC_MS, Math.max(0, REFILL_SETTLE_MS - 16)), () => { syncRefilledBoard(false); });
          scheduleResolutionStep(showRefillDelay + REFILL_SETTLE_MS, () => {
            const cascade = runtime.matchIndexes();
            if (cascade.length > 0) {
              resolveMatched(cascade, cascadeDepth + 1);
              return;
            }
            const end = runtime.completeMove();
            const ended = end.outcome === 'win';
            const previousPlan = metaRef.current.lastResolutionPlan;
            updateMeta({
              ended,
              resolving: false,
              resolutionPhase: 'complete',
              lastResolutionPlan: previousPlan && previousPlan.result === 'accepted'
                ? { ...previousPlan, finalBoard: runtime.board.cells.slice(), outcome: end.outcome }
                : previousPlan,
            }, { render: true });
            if (ended) {
              emit(match3TelemetryEvents.ctaShown, asJson({ topologyId: payload.level.topologyId }));
              emit(match3TelemetryEvents.endcardVisible, asJson({ topologyId: payload.level.topologyId }));
              // A5: Win celebration — confetti burst + CTA pulse loop.
              animateWinCelebration();
              startCtaPulse();
              winSting(); // A6: win sting SFX.
            }
          });
        });
      };
      scheduleResolutionStep(SWAP_MS, () => {
        const swappedAndMatched = [...new Set([move.fromIndex, move.toIndex, ...accepted.matchedIndexes])];
        setVisualBoard(runtime.board.cells.slice());
        syncTileLayersToRuntimeBoard(swappedAndMatched);
        resolveMatched(accepted.matchedIndexes);
      });
    });
    return () => {
      unregisterDown();
      unregisterUp();
    };
  }, [allCellIndexes, animateCascadeCombo, animateDenyShake, animateFlowerChanges, animateRefillDrop, animateReturn, animateSpecialBlast, animateSwap, animateTileClear, animateWinCelebration, changedFlowerTargets, cells, emit, onInput, payload.level.board.cols, payload.level.board.rows, payload.level.topologyId, payload.skin.slots.tileTypes, runtime, scheduleResolutionStep, startCtaPulse, syncTileLayersToRuntimeBoard, tileAssetById, updateMeta]);

  // @intent Render home-position objective flower sprites and separate in-flight collect sprites (Defect A+C fix).
  // @flow runtime.objectiveTargets() → split by role:
  //         role='blocker' → blockerStages texture per stage (Б3 staticSprites path);
  //         other roles    → objectiveStages flower path (existing logic).
  //       collected → last stage + dim decoration (not invisible hole, Defect A);
  //       flightSprites state → dedicated flight NativeSprite layers tweened independently (Defect C fix).
  // @stale 2026-06-11 — Б3: blocker branch added; DO NOT touch special/blast code paths.
  const staticSprites = runtime.objectiveTargets().map((target) => {
    const cell = cells[target.index];
    const staticCell = staticByIndex.get(target.index);
    if (!cell || !staticCell) return null;

    // Б3: blocker role — use blockerStages texture pool keyed by current stage.
    if (staticCell.role === 'blocker') {
      const blockerPool = payload.skin.slots.blockerStages;
      if (!blockerPool || blockerPool.length === 0) return null; // no textures → don't render
      const stageIndex = target.collected
        ? Math.max(0, blockerPool.length - 1)
        : Math.max(0, Math.min(blockerPool.length - 1, Math.trunc(target.stage)));
      const blockerAssetUrl = blockerPool[stageIndex] ?? blockerPool[0] ?? '';
      return (
        <NativeSprite
          key={`match-static-${String(target.index)}`}
          id={`match-static-${String(target.index)}`}
          assetUrl={blockerAssetUrl}
          x={cell.center.x}
          y={cell.center.y}
          width={cell.visual.width}
          height={cell.visual.height}
          anchor={{ x: 0.5, y: 0.5 }}
          visible
          registerTarget={registerNativeTarget}
          onLayerSelected={onLayerSelected}
        />
      );
    }

    // Flower/grow path (original logic — untouched).
    // After collection show the final-stage sprite dimmed (decorative collected state) instead of an invisible
    // dead hole. Input remains blocked (engine blockedIndexes always includes static cell positions).
    const effectiveStage = target.collected
      ? Math.max(0, payload.skin.slots.objectiveStages.length - 1)
      : Math.max(0, Math.min(payload.skin.slots.objectiveStages.length - 1, Math.trunc(target.stage)));
    const flower = flowerVisual(target.collected
      ? { index: target.index, stage: target.maxStage }
      : target);
    if (!flower) return null;
    return (
      <NativeSprite
        key={`match-static-${String(target.index)}`}
        id={`match-static-${String(target.index)}`}
        assetUrl={payload.skin.slots.objectiveStages[effectiveStage] ?? payload.skin.slots.objectiveStages[0] ?? ''}
        x={flower.x}
        y={flower.y}
        width={flower.width}
        height={flower.height}
        anchor={{ x: 0.5, y: 0.5 }}
        visible
        registerTarget={registerNativeTarget}
        onLayerSelected={onLayerSelected}
      />
    );
  });

  // @intent Dedicated collect-flight sprites, decoupled from home sprites so React prop updates on the home
  //         sprite cannot override the tween's in-flight position (Defect C fix).
  // @flow Mounted when animateFlowerChanges sets flightSprites state; tweened toward HUD anchor; unmounted
  //       (via setFlightSprites filter) when scheduleResolutionStep(FLOWER_FLY_MS) fires on completion.
  // @stale 2026-06-10 — transient; exists only for the duration of a collect-flight animation.
  const collectFlightSprites = flightSprites.map((fs) => (
    <NativeSprite
      key={`match-static-fly-${String(fs.index)}`}
      id={`match-static-fly-${String(fs.index)}`}
      assetUrl={fs.assetUrl}
      x={fs.x}
      y={fs.y}
      width={fs.width}
      height={fs.height}
      anchor={{ x: 0.5, y: 0.5 }}
      visible
      registerTarget={registerNativeTarget}
      onLayerSelected={onLayerSelected}
    />
  ));

  // @intent A2: short-lived clear-fx particle sprites — mini-copies of cleared tile textures mounted via state,
  //         tweened outward + faded, unmounted on completion via functional setClearFxSprites filter.
  // @flow clearFxSprites state -> NativeSprite per particle; pending-tween drain in registerNativeTarget starts
  //       the radial tween on mount; tween finished.finally removes each particle on completion.
  // @stale 2026-06-11 — A2: clear particles from tile textures.
  const clearFxSpritesJsx = clearFxSprites.map((fx) => (
    <NativeSprite
      key={fx.layerId}
      id={fx.layerId}
      assetUrl={fx.assetUrl}
      x={fx.x}
      y={fx.y}
      width={fx.width}
      height={fx.height}
      anchor={{ x: 0.5, y: 0.5 }}
      visible
      registerTarget={registerNativeTarget}
      onLayerSelected={onLayerSelected}
    />
  ));

  const tileSprites = cells.map((cell) => {
    if (staticIndexSet.has(cell.tileIndex)) return null;
    const tileType = visualBoard[cell.tileIndex] ?? -1;
    if (tileType < 0) return null;
    const tileId = runtime.tileIdForType(tileType);
    return (
      <NativeSprite
        key={`match-tile-${String(cell.tileIndex)}`}
        id={`match-tile-${String(cell.tileIndex)}`}
        assetUrl={tileAssetById.get(tileId) ?? payload.skin.slots.tileTypes[0] ?? ''}
        x={cell.center.x}
        y={cell.center.y}
        width={cell.visual.width}
        height={cell.visual.height}
        anchor={{ x: 0.5, y: 0.5 }}
        registerTarget={registerNativeTarget}
        onLayerSelected={onLayerSelected}
      />
    );
  });

  // Б1: Special tile overlays — programmatic graphics drawn over the tile sprite for each active special.
  // @intent No new art slots; overlays are purely programmatic (stripe/glow via pixiGraphics).
  //         rocket(H/V) = two chevron stripes along the blast axis; bomb = dark radial ring; colorBomb = concentric circles.
  //         Overlays are NOT separate input layers (no eventMode) — they live inside the tile footprint.
  // @flow specialsMapRef.current → pixiGraphics per cell; re-drawn each render revision via draw callback.
  // @stale 2026-06-11 — Б1: special tile overlay graphics.
  const specialOverlays = cells.map((cell) => {
    if (staticIndexSet.has(cell.tileIndex)) return null;
    const kind = specialsMapRef.current.get(cell.tileIndex);
    if (!kind) return null;
    const cx = cell.center.x;
    const cy = cell.center.y;
    const hw = cell.visual.width / 2;
    const hh = cell.visual.height / 2;
    const stripeW = cell.visual.width * 0.28;
    const stripeH = cell.visual.height * 0.28;
    return (
      <pixiGraphics
        key={`match-special-overlay-${String(cell.tileIndex)}`}
        draw={(g) => {
          g.clear();
          if (kind === 'rocketH' || kind === 'rocket') {
            // Two horizontal chevron stripes.
            g.rect(cx - hw * 0.85, cy - stripeH / 2, hw * 1.7, stripeH);
            g.fill({ color: 0xffffff, alpha: 0.75 });
            g.rect(cx - hw * 0.85, cy - stripeH / 2 - stripeH * 1.3, hw * 1.7, stripeH * 0.55);
            g.fill({ color: 0xffffff, alpha: 0.45 });
          } else if (kind === 'rocketV') {
            // Two vertical chevron stripes.
            g.rect(cx - stripeW / 2, cy - hh * 0.85, stripeW, hh * 1.7);
            g.fill({ color: 0xffffff, alpha: 0.75 });
            g.rect(cx - stripeW / 2 - stripeW * 1.3, cy - hh * 0.85, stripeW * 0.55, hh * 1.7);
            g.fill({ color: 0xffffff, alpha: 0.45 });
          } else if (kind === 'bomb') {
            // Dark radial ring.
            g.circle(cx, cy, hw * 0.72);
            g.fill({ color: 0x220044, alpha: 0.55 });
            g.circle(cx, cy, hw * 0.72);
            g.stroke({ color: 0xff4400, alpha: 0.85, width: 2 });
          } else if (kind === 'colorBomb') {
            // Rainbow concentric circles using a fixed palette.
            const palette = [0xff2222, 0xff8800, 0xffee00, 0x22dd44, 0x2266ff, 0xcc44ff];
            const radii = [hw * 0.3, hw * 0.46, hw * 0.58, hw * 0.68, hw * 0.76, hw * 0.82];
            for (let i = 0; i < palette.length; i += 1) {
              g.circle(cx, cy, radii[i] ?? hw * 0.3);
              g.stroke({ color: palette[i] ?? 0xffffff, alpha: 0.7, width: 2 });
            }
          }
        }}
      />
    );
  });

  // Б1: Blast-fx sprites — short-lived programmatic graphic layers for rocket sweeps and bomb rings.
  // @intent id prefix `match-blast-fx-` → classified as 'effect' by layerSemanticRole in both evidence files.
  // @flow blastFxSprites state → pixiGraphics per blast; pending-tween drain in registerNativeTarget starts
  //       alpha/size tween on mount; tween finished.finally removes each sprite on completion.
  // @stale 2026-06-11 — Б1: blast flash graphics (sweep + ring).
  const blastFxSpritesJsx = blastFxSprites.map((bfx) => (
    <pixiGraphics
      key={bfx.layerId}
      ref={(g) => {
        if (g) registerNativeTarget(bfx.layerId, g as unknown as Record<string, unknown>);
      }}
      draw={(g) => {
        g.clear();
        if (bfx.kind === 'sweep-h' || bfx.kind === 'sweep-v') {
          g.rect(bfx.x - bfx.width / 2, bfx.y - bfx.height / 2, bfx.width, bfx.height);
          g.fill({ color: bfx.color, alpha: 0.85 });
        } else {
          // ring
          g.circle(bfx.x, bfx.y, bfx.width / 2);
          g.stroke({ color: bfx.color, alpha: 0.7, width: 4 });
        }
      }}
    />
  ));

  // @intent Render boardPanel as a programmatic rounded-rect graphic instead of the generated image.
  //         presentation.boardPanel='none' suppresses the panel entirely (transparent bg skins).
  //         presentation.boardPanel='plain' (default) renders the dark fill+border programmatic panel.
  //         The generated boardPanel image contains a BAKED cell grid at the AI's assumed rows×cols — when that
  //         differs from the level's actual topology the two grids compete visually (Fix 1 defect: two grids).
  //         A plain dark fill + border stroke behind our programmatic socket underlays is always correct.
  //         skin.slots.boardPanel asset is kept in the data contract but is NOT rendered (@stale below).
  // @flow viewModel.layout.board -> pixiGraphics rounded rect at (board.x - inset, board.y - inset).
  //       skin.presentation.boardPanel controls whether the panel is rendered.
  // @stale 2026-06-10 — boardPanel image suppressed here; future art should be a plain decorative FRAME
  //         (no internal cell grid) per updated image-prompt wording in match3-skin-builder; re-enable as
  //         NativeSprite once art reliably delivers frame-only images.
  //         2026-06-12 — presentation.boardPanel='none' added for transparent-bg skins (P14 port).
  const presShowBoardPanel = (payload.skin.presentation?.boardPanel ?? 'plain') !== 'none';
  const boardInset = Math.round(Math.max(0, payload.skin.fit.boardInsetPx) * (1 + BOARD_PANEL_INSET_EXTRA_RATIO));
  const boardPanelX = viewModel.layout.board.x - boardInset;
  const boardPanelY = viewModel.layout.board.y - boardInset;
  const boardPanelW = viewModel.layout.board.width + boardInset * 2;
  const boardPanelH = viewModel.layout.board.height + boardInset * 2;
  const boardPanelCornerR = Math.round(Math.min(boardPanelW, boardPanelH) * BOARD_PANEL_CORNER_RADIUS_RATIO);
  const boardPanelGraphic = presShowBoardPanel ? (
    <pixiGraphics
      draw={(g) => {
        g.clear();
        g.roundRect(boardPanelX, boardPanelY, boardPanelW, boardPanelH, boardPanelCornerR);
        g.fill({ color: BOARD_PANEL_FILL, alpha: BOARD_PANEL_ALPHA });
        g.roundRect(boardPanelX, boardPanelY, boardPanelW, boardPanelH, boardPanelCornerR);
        g.stroke({ color: BOARD_PANEL_BORDER_COLOR, alpha: BOARD_PANEL_BORDER_ALPHA, width: BOARD_PANEL_BORDER_WIDTH });
      }}
    />
  ) : null;

  // @intent Render per-cell socket underlays (programmatic rounded rects) so tiles always visually land in
  //         their cell sockets regardless of what the boardPanel image contains. This decouples tile placement
  //         from any baked socket grid in the panel art (layout defect fix).
  //         presentation.cellChecker: alternating (row+col)%2 checker in colorA/colorB instead of dark sockets.
  //         presentation.cellChecker='none': skip ALL per-cell socket/checker drawing entirely.
  // @flow cells -> pixiGraphics rounded rect at each cell.visual rect; drawn below tile sprites.
  //       skin.presentation.cellChecker controls fill color (colorA for even cells, colorB for odd).
  //       cellChecker==='none' → skip entire underlay pass (null graphic rendered).
  // @stale 2026-06-10 — zero new contract fields; uses existing computeMatch3CellRects cells array.
  //        Permanent holes (level.board.cells[i] === null, not a static host) get NO socket — irregular
  //        silhouettes must read as absent cells, not empty sockets (study art-rule: holes are board shape).
  //        2026-06-12 — presentation.cellChecker added for warm-tone checker underlays (P14 port).
  //        2026-06-15 — cellChecker='none' skips all underlay drawing; tileShadow pass added between underlays and tiles.
  const presCellChecker = payload.skin.presentation?.cellChecker;
  const presTileShadow = payload.skin.presentation?.tileShadow ?? false;
  // @intent Constants for per-cell drop shadow (tileShadow presentation flag).
  // @flow dark rounded-rect rendered offset (+3px x, +4px y) per non-hole non-static cell; corner radius matches tile.
  // @stale 2026-06-15 — V1.2 tileShadow additive feature.
  const CELL_SHADOW_OFFSET_X = 3;
  const CELL_SHADOW_OFFSET_Y = 4;
  const CELL_SHADOW_FILL = 0x000000;
  const CELL_SHADOW_ALPHA = 0.22;
  const cellSocketUnderlays = presCellChecker === 'none' ? null : (
    <pixiGraphics
      draw={(g) => {
        g.clear();
        for (const cell of cells) {
          const isPermanentHole = (payload.level.board.cells[cell.tileIndex] === null
            || payload.level.board.enabledMask?.[cell.tileIndex] === false)
            && !staticIndexSet.has(cell.tileIndex);
          if (isPermanentHole) continue;
          const r = Math.round(cell.visual.width * CELL_SOCKET_CORNER_RADIUS_RATIO);
          g.roundRect(cell.visual.x, cell.visual.y, cell.visual.width, cell.visual.height, r);
          if (typeof presCellChecker === 'object' && presCellChecker !== null) {
            // Checker: even cells (row+col even) get colorA, odd cells get colorB.
            const isEven = (cell.row + cell.col) % 2 === 0;
            const hexStr = isEven ? presCellChecker.colorA : presCellChecker.colorB;
            const hexNum = parseInt(hexStr.replace('#', ''), 16);
            g.fill({ color: hexNum, alpha: presCellChecker.alpha });
          } else {
            g.fill({ color: CELL_SOCKET_FILL, alpha: CELL_SOCKET_ALPHA });
          }
        }
      }}
    />
  );

  // @intent Per-cell drop shadow pass: soft dark rounded-rect rendered offset below each non-hole non-static cell.
  //         Drawn BETWEEN cellSocketUnderlays and tileSprites so shadows appear under tiles but over the board panel.
  //         Gated behind presentation.tileShadow (default false / off for all existing skins).
  // @flow skin.presentation.tileShadow===true -> pixiGraphics with per-cell shadow rects at (+3px x, +4px y) offset;
  //       corner radius matches CELL_SOCKET_CORNER_RADIUS_RATIO; alpha ~0.22; dark fill 0x000000.
  // @stale 2026-06-15 — V1.2 additive; absent/false = no shadow (back-compat).
  const tileShadowLayer = presTileShadow ? (
    <pixiGraphics
      draw={(g) => {
        g.clear();
        for (const cell of cells) {
          const isPermanentHole = (payload.level.board.cells[cell.tileIndex] === null
            || payload.level.board.enabledMask?.[cell.tileIndex] === false)
            && !staticIndexSet.has(cell.tileIndex);
          if (isPermanentHole) continue;
          const r = Math.round(cell.visual.width * CELL_SOCKET_CORNER_RADIUS_RATIO);
          g.roundRect(
            cell.visual.x + CELL_SHADOW_OFFSET_X,
            cell.visual.y + CELL_SHADOW_OFFSET_Y,
            cell.visual.width,
            cell.visual.height,
            r,
          );
          g.fill({ color: CELL_SHADOW_FILL, alpha: CELL_SHADOW_ALPHA });
        }
      }}
    />
  ) : null;

  // @intent Render a programmatic HUD pill with objective icon + remaining counter instead of the generated
  //         hudPanel image (Fix 3). The generated hudPanel art was stretched over the wrong rect (hardcoded
  //         y:24 h:54 in the scene-graph vs the runtime layout.hud band) and shows no readable objective info.
  //         A plain rounded-rect pill with a small tile-stage icon and a live counter is always legible.
  //         skin.slots.hudPanel asset is kept in the data contract but is NOT rendered (@stale below).
  //         presentation.hudVisible=false suppresses the entire HUD band (pill/icon/counter/title).
  // @flow viewModel.layout.hud -> pixiGraphics pill + NativeSprite icon at pilll-left + NativeText counter.
  //       objectiveRemaining is read from runtime each render (revision dependency keeps it fresh).
  //       skin.presentation.hudVisible gates whether the HUD is rendered (default true).
  // @stale 2026-06-10 — hudPanel image suppressed; future art should be a transparent pill overlay; re-enable
  //         as NativeSprite once art reliably fits the hud band without letterboxing or stretching.
  //         2026-06-12 — presentation.hudVisible=false added for skins with bespoke HUD art (P14 port).
  const presHudVisible = payload.skin.presentation?.hudVisible ?? true;
  const hudH = viewModel.layout.hud.height;
  const hudY = viewModel.layout.hud.y;
  const hudX = viewModel.layout.hud.x;
  const hudW = viewModel.layout.hud.width;
  // Pill is horizontally centered, slightly inset from the full hud band width.
  const pillPadV = Math.round(hudH * 0.12);
  const pillH = hudH - pillPadV * 2;
  const pillW = Math.round(Math.min(hudW * 0.6, Math.max(120, pillH * 3.2)));
  const pillX = hudX + Math.round((hudW - pillW) / 2);
  const pillY = hudY + pillPadV;
  const pillCornerR = Math.round(pillH / 2);
  const iconSize = Math.round(pillH * 0.72);
  const iconPadL = Math.round(pillH * 0.18);
  const iconCX = pillX + iconPadL + iconSize / 2;
  const iconCY = pillY + pillH / 2;
  const counterFontSize = Math.max(14, Math.round(pillH * 0.52));
  const counterX = pillX + pillW * 0.62;
  const counterY = pillY + pillH / 2;
  const objectiveRemainingNow = runtime.objectiveRemaining();
  // Collect-flight anchor is the icon center so flying flowers converge on the pill icon (Fix 3 intent).
  // flowerFlyTarget() already returns viewModel.layout.hud center — it will naturally land near the pill icon.
  const hudPill = presHudVisible ? (
    <pixiGraphics
      draw={(g) => {
        g.clear();
        g.roundRect(pillX, pillY, pillW, pillH, pillCornerR);
        g.fill({ color: HUD_PILL_FILL, alpha: HUD_PILL_ALPHA });
        g.roundRect(pillX, pillY, pillW, pillH, pillCornerR);
        g.stroke({ color: HUD_PILL_BORDER_COLOR, alpha: HUD_PILL_BORDER_ALPHA, width: HUD_PILL_BORDER_WIDTH });
      }}
    />
  ) : null;
  // @intent Pick the HUD icon based on objective role so non-flower/grow objectives don't show a flower icon.
  // @flow flower/grow → objectiveStages[0] (the flower/host art); collectN tiles/other → tileTypes[0]
  //       (the dominant tile asset); never objectiveStages when the level has no static flower hosts.
  //       Rationale: objectiveStages is flower-stage art — meaningless for tile-collection levels.
  // @stale 2026-06-11 — fix: was always objectiveStages[0] regardless of objective type (showed flower icon
  //         on ice/collectN tile levels; project 56a87526 regression).
  const objectiveRole = payload.level.objective.role ?? '';
  const isFlowerObjective = objectiveRole === 'flower' || objectiveRole === 'grow' || payload.level.objective.type === 'grow';
  const hudIconAsset = isFlowerObjective
    ? (payload.skin.slots.objectiveStages[0] ?? '')
    : (payload.skin.slots.tileTypes[0] ?? payload.skin.slots.objectiveStages[0] ?? '');
  const hudIcon = presHudVisible ? (
    <NativeSprite
      id="native-match3-hud-icon"
      assetUrl={hudIconAsset}
      x={iconCX}
      y={iconCY}
      width={iconSize}
      height={iconSize}
      anchor={{ x: 0.5, y: 0.5 }}
      visible
      registerTarget={registerNativeTarget}
      onLayerSelected={onLayerSelected}
    />
  ) : null;
  const hudCounter = presHudVisible ? (
    <NativeText
      id="native-match3-hud-counter"
      text={String(objectiveRemainingNow)}
      x={counterX}
      y={counterY}
      fontSize={counterFontSize}
      fill="#ffffff"
      visible
      registerTarget={registerNativeTarget}
    />
  ) : null;

  // @intent Render game title text on the left side of the HUD band (P16c Fix 3).
  //         The scene-graph native-match3-title layer at y:39 was invisible under the phone-frame notch;
  //         we suppress it and draw programmatically at hud.y + hudH/2 (always >= SAFE_AREA_TOP=44).
  //         Title is left-aligned with 12px padding from hud.x; font shrinks to avoid overlapping the pill.
  //         Suppressed entirely when presentation.hudVisible=false.
  // @flow designTitle -> pixiText at (hudX+12, hudY+hudH/2) anchor=(0,0.5); maxWidth=pillX-hudX-24.
  //       presHudVisible gates whether the title is rendered.
  // @stale 2026-06-11 — added to replace scene-graph native-match3-title (P16c).
  //         2026-06-12 — gated on presHudVisible (presentation.hudVisible).
  const titleText = presHudVisible && designTitle != null && designTitle.length > 0 ? (() => {
    const titlePadL = 12;
    const titleX = hudX + titlePadL;
    const titleY = hudY + hudH / 2;
    // Keep title font size modest so it never bleeds into the pill area.
    const maxTitleW = Math.max(0, pillX - titleX - 8);
    const rawTitleFontSize = Math.max(12, Math.round(hudH * 0.38));
    // Approximate character width at rawTitleFontSize (0.55× font size per glyph).
    const approxCharW = rawTitleFontSize * 0.55;
    const maxCharsAtRaw = maxTitleW > 0 ? Math.floor(maxTitleW / approxCharW) : designTitle.length;
    // Prefer one font-step down (85%) before truncating so short titles stay whole.
    const smallFontSize = Math.max(12, Math.round(rawTitleFontSize * 0.85));
    const smallCharW = smallFontSize * 0.55;
    const maxCharsAtSmall = maxTitleW > 0 ? Math.floor(maxTitleW / smallCharW) : designTitle.length;
    const fitsAtSmall = designTitle.length <= maxCharsAtSmall;
    const titleFontSize = fitsAtSmall ? smallFontSize : rawTitleFontSize;
    const maxChars = fitsAtSmall ? maxCharsAtSmall : maxCharsAtRaw;
    const displayTitle = truncateTitle(designTitle, maxChars);
    return displayTitle.length > 0 ? (
      <NativeText
        id="native-match3-title"
        text={displayTitle}
        x={titleX}
        y={titleY}
        fontSize={titleFontSize}
        fill="#ffffff"
        visible
        anchor={{ x: 0, y: 0.5 }}
        registerTarget={registerNativeTarget}
      />
    ) : null;
  })() : null;

  // @intent Bottom banner sprite displayed before first accepted move; fades out 200ms after firstInteraction.
  //         presentation.banner.assetUrl is the sprite; heightPx controls the sprite height at viewport bottom.
  //         Fades via alpha tween then unmounts (bannerGone state); uses setTimeout after tweenLayer since
  //         tweenLayer returns void — fade duration is known so a matching setTimeout unmounts after fade.
  // @flow skin.presentation.banner -> NativeSprite id='match-banner' centered at (vpW/2, vpH - heightPx/2 - 8).
  //       hadFirstInteraction true -> tweenLayer alpha→0 over 200ms, setTimeout(200ms) sets bannerGone=true.
  // @stale 2026-06-12 — presentation.banner feature (P14 port).
  const presBanner = payload.skin.presentation?.banner;
  const bannerFadeMs = 200;
  const [bannerGone, setBannerGone] = React.useState(false);
  const bannerFadedRef = React.useRef(false);
  React.useEffect(() => {
    if (!presBanner || bannerFadedRef.current || !hadFirstInteraction) return;
    bannerFadedRef.current = true;
    tweenLayer('match-banner', { alpha: 0 }, { durationMs: bannerFadeMs, easing: 'easeInQuad' });
    const timer = setTimeout(() => { setBannerGone(true); }, bannerFadeMs + 16);
    return () => { clearTimeout(timer); };
  }, [hadFirstInteraction, presBanner, tweenLayer]);
  const bannerSprite = presBanner != null && !bannerGone ? (
    <NativeSprite
      id="match-banner"
      assetUrl={presBanner.assetUrl}
      x={designViewport.width / 2}
      y={designViewport.height - presBanner.heightPx / 2 - 8}
      width={designViewport.width}
      height={presBanner.heightPx}
      anchor={{ x: 0.5, y: 0.5 }}
      visible
      registerTarget={registerNativeTarget}
      onLayerSelected={onLayerSelected}
    />
  ) : null;

  // @intent Ambient decoration sprites with idle bob tween (y ±6px, ~600ms up/down, ease in-out, looping).
  //         Each sprite is identified as `match-ambient-N` so probe report classifies it as 'effect'
  //         (never gameplay or board churn). Bob re-triggers via setTimeout chain (tweenLayer returns void,
  //         so we use duration-based setTimeout to chain the three bob phases: up → down → center → repeat).
  // @flow skin.presentation.ambientSprites -> NativeSprite array; bob tween started on mount via pending-tween
  //       pattern (pendingAmbientTweensRef keyed by layerId). Re-trigger: setTimeout after each phase.
  // @stale 2026-06-12 — presentation.ambientSprites feature (P14 port).
  //   Cross-@stale: runtime-probe-report.ts and runtime-probe-scenario.mjs layerSemanticRole must classify
  //   match-ambient- ids as 'effect' — mirrored in both files; keep in sync.
  const presAmbientSprites = payload.skin.presentation?.ambientSprites ?? [];
  const pendingAmbientTweensRef = React.useRef(new Map<string, () => void>());
  const ambientBobLoopRef = React.useRef(new Map<string, ReturnType<typeof setTimeout> | null>());
  React.useEffect(() => {
    const BOB_HALF_MS = 600;
    const BOB_PX = 6;
    const startBob = (layerId: string, baseY: number) => {
      // Up phase.
      tweenLayer(layerId, { y: baseY - BOB_PX }, { durationMs: BOB_HALF_MS, easing: 'easeInOutQuad' });
      const t1 = setTimeout(() => {
        // Down phase.
        tweenLayer(layerId, { y: baseY + BOB_PX }, { durationMs: BOB_HALF_MS, easing: 'easeInOutQuad' });
        const t2 = setTimeout(() => {
          // Return to center.
          tweenLayer(layerId, { y: baseY }, { durationMs: BOB_HALF_MS, easing: 'easeInOutQuad' });
          const t3 = setTimeout(() => {
            if (ambientBobLoopRef.current.has(layerId)) startBob(layerId, baseY);
          }, BOB_HALF_MS);
          ambientBobLoopRef.current.set(layerId, t3);
        }, BOB_HALF_MS);
        ambientBobLoopRef.current.set(layerId, t2);
      }, BOB_HALF_MS);
      ambientBobLoopRef.current.set(layerId, t1);
    };
    for (const [index, sprite] of presAmbientSprites.entries()) {
      const layerId = `match-ambient-${String(index)}`;
      if (ambientBobLoopRef.current.has(layerId)) continue;
      // Register as active (null = registered but not yet started; pending tween starts on NativeSprite mount).
      ambientBobLoopRef.current.set(layerId, null);
      const capturedY = sprite.y;
      pendingAmbientTweensRef.current.set(layerId, () => { startBob(layerId, capturedY); });
    }
    return () => {
      for (const [, timer] of ambientBobLoopRef.current) {
        if (timer !== null) clearTimeout(timer);
      }
      ambientBobLoopRef.current.clear();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presAmbientSprites.length, tweenLayer]);
  const ambientSpritesJsx = presAmbientSprites.map((sprite, index) => {
    const layerId = `match-ambient-${String(index)}`;
    return (
      <NativeSprite
        key={layerId}
        id={layerId}
        assetUrl={sprite.assetUrl}
        x={sprite.x}
        y={sprite.y}
        width={sprite.size}
        height={sprite.size}
        anchor={{ x: 0.5, y: 0.5 }}
        visible
        registerTarget={(id, target) => {
          // Drain pending bob tween on mount (same pattern as pendingFlightTweensRef).
          const pending = pendingAmbientTweensRef.current.get(id);
          if (pending) {
            pendingAmbientTweensRef.current.delete(id);
            pending();
          }
          return registerNativeTarget(id, target);
        }}
        onLayerSelected={onLayerSelected}
      />
    );
  });

  // @intent Render background with true cover-fit when slots.backgroundSize is provided; falls back to
  //         stretch-to-viewport for legacy skins without the size hint.
  //         Cover-fit: scale = max(vpW/texW, vpH/texH) → uniform scale, centered, overflow cropped.
  //         The scene-graph version (native-match3-background) is suppressed via NATIVE_MATCH3_OWNED_LAYER_IDS.
  // @flow skin.slots.backgroundSize {w,h} present → compute cover scale → sprite sized to texW*scale × texH*scale
  //       centered at vpW/2, vpH/2 (anchor 0.5); overflow is clipped naturally by Pixi.
  //       backgroundSize absent → legacy stretch to vpW×vpH (exact prior behavior; no regression).
  // @stale 2026-06-10 — Fix 2: suppress + own added to fix background contain/letterbox defect.
  //         2026-06-12 — bg.jpg 540×960 asset fits 390×844 via cover math correctly.
  //         2026-06-15 — backgroundSize hint enables true cover math (scale by max ratio, not stretch).
  const vpW = designViewport.width;
  const vpH = designViewport.height;
  // Pure cover-fit math: given intrinsic texture size, compute the scale factor that fills the viewport
  // by the larger ratio so neither dimension is shorter than the viewport (no letterbox bands).
  // formula: scale = max(vpW / texW, vpH / texH); rendered size = texW*scale × texH*scale.
  const bgSize = payload.skin.slots.backgroundSize;
  const bgCoverW = bgSize !== undefined
    ? bgSize.w * Math.max(vpW / bgSize.w, vpH / bgSize.h)
    : vpW;
  const bgCoverH = bgSize !== undefined
    ? bgSize.h * Math.max(vpW / bgSize.w, vpH / bgSize.h)
    : vpH;
  const backgroundSprite = (
    <NativeSprite
      id="native-match3-background"
      assetUrl={payload.skin.slots.background}
      x={vpW / 2}
      y={vpH / 2}
      width={bgCoverW}
      height={bgCoverH}
      anchor={{ x: 0.5, y: 0.5 }}
      visible
      registerTarget={registerNativeTarget}
      onLayerSelected={onLayerSelected}
    />
  );

  const ended = metaRef.current.ended;
  const rewardSize = Math.round(Math.max(96, Math.min(
    viewModel.layout.endcard.width * 0.58,
    viewModel.layout.endcard.height * 0.28,
    238,
  )));
  const rewardCenterY = Math.round(viewModel.layout.endcard.y + viewModel.layout.endcard.height * 0.42);
  const endcardBackdrop = payload.skin.slots.reward === undefined ? null : (
    <NativeSprite
      id="native-match3-endcard-backdrop"
      assetUrl={payload.skin.slots.background}
      x={viewModel.layout.endcard.x + viewModel.layout.endcard.width / 2}
      y={viewModel.layout.endcard.y + viewModel.layout.endcard.height / 2}
      width={viewModel.layout.endcard.width}
      height={viewModel.layout.endcard.height}
      anchor={{ x: 0.5, y: 0.5 }}
      visible={ended}
      registerTarget={registerNativeTarget}
      onLayerSelected={onLayerSelected}
    />
  );
  const endcardSprite = (
    <NativeSprite
      id="native-match3-endcard"
      assetUrl={payload.skin.slots.reward ?? payload.skin.slots.background}
      x={viewModel.layout.endcard.x + viewModel.layout.endcard.width / 2}
      y={payload.skin.slots.reward === undefined ? viewModel.layout.endcard.y + viewModel.layout.endcard.height / 2 : rewardCenterY}
      width={payload.skin.slots.reward === undefined ? viewModel.layout.endcard.width : rewardSize}
      height={payload.skin.slots.reward === undefined ? viewModel.layout.endcard.height : rewardSize}
      anchor={{ x: 0.5, y: 0.5 }}
      visible={ended}
      registerTarget={registerNativeTarget}
      onLayerSelected={onLayerSelected}
    />
  );
  const ctaSprite = (
    <NativeSprite
      id="native-match3-cta"
      assetUrl={payload.skin.slots.ctaButton}
      x={viewModel.layout.cta.x + viewModel.layout.cta.width / 2}
      y={viewModel.layout.cta.y + viewModel.layout.cta.height / 2}
      width={viewModel.layout.cta.width}
      height={viewModel.layout.cta.height}
      anchor={{ x: 0.5, y: 0.5 }}
      visible={ended}
      registerTarget={registerNativeTarget}
      onLayerSelected={onLayerSelected}
    />
  );
  const ctaLabel = (
    <NativeText
      id="native-match3-cta-label"
      text="PLAY NOW"
      x={viewModel.layout.cta.x + viewModel.layout.cta.width / 2}
      y={viewModel.layout.cta.y + viewModel.layout.cta.height / 2}
      fontSize={Math.max(16, Math.min(24, Math.round(viewModel.layout.cta.height * 0.28)))}
      fill="#ffffff"
      visible={ended}
      registerTarget={registerNativeTarget}
    />
  );

  // A4: Cascade combo popup — NativeText centered on board, mounted only when comboPopup state is set.
  // @intent Scale-tweened via pending-tween-on-mount pattern; unmounted on fade complete.
  // @stale 2026-06-11 — A4: combo popup JSX.
  const comboPopupJsx = comboPopup != null ? (
    <NativeText
      key={comboPopup.id}
      id={comboPopup.id}
      text={comboPopup.label}
      x={viewModel.layout.board.x + viewModel.layout.board.width / 2}
      y={viewModel.layout.board.y + viewModel.layout.board.height / 2}
      fontSize={Math.max(24, Math.round(viewModel.layout.board.cellSize * 0.55))}
      fill="#ffffff"
      visible
      registerTarget={registerNativeTarget}
    />
  ) : null;

  // A5: Confetti graphic — single pixiGraphics with stable layout from confettiLayoutRef.
  // @intent Mounted on win for 1200ms; uses stable ref values so draw callback does not re-randomize per frame.
  // @stale 2026-06-11 — A5: win confetti burst.
  const confettiGraphic = confettiVisible ? (
    <pixiGraphics
      draw={(g) => {
        g.clear();
        for (const piece of confettiLayoutRef.current) {
          g.rect(piece.x, piece.y, piece.w, piece.h);
          g.fill({ color: piece.color });
        }
      }}
    />
  ) : null;

  // Collectible underlays (jelly-under-tile): wafer sprite (objectiveStages[0]) drawn UNDER the normal
  // swappable tile at each remaining underlay cell, so the board reads as a dense packed grid (reference
  // parity). These cells keep their swappable tile on top (rendered by tileSprites). Re-reads runtime each
  // render; underlays drop out as they are collected (revision bump re-renders).
  // @stale 2026-06-15 — collectible-underlay engine feature (0f18e53); texture = skin.slots.objectiveStages[0].
  const cellByIndex = new Map(cells.map((c) => [c.tileIndex, c] as const));
  const underlayTexture = payload.skin.slots.objectiveStages[0] ?? '';
  const underlaySprites = underlayTexture
    ? runtime.getUnderlayIndexes().map(({ index }) => {
        const cell = cellByIndex.get(index);
        if (!cell) return null;
        return (
          <NativeSprite
            key={`match-underlay-${String(index)}`}
            id={`match-underlay-${String(index)}`}
            assetUrl={underlayTexture}
            x={cell.center.x}
            y={cell.center.y}
            // 0.56× so the square underlay hides BEHIND the round button (its corners no longer poke
            // through the tile's transparent corners); it is revealed only when the tile clears.
            width={cell.visual.width * 0.56}
            height={cell.visual.height * 0.56}
            anchor={{ x: 0.5, y: 0.5 }}
            visible
            registerTarget={registerNativeTarget}
            onLayerSelected={onLayerSelected}
          />
        );
      })
    : [];

  // Tutorial hand: gloved-hand sprite at the scripted firstMove FROM-cell while no interaction yet (the
  // reference shows a hand pointing at the guided swap). Hidden after the first accepted move.
  // @stale 2026-06-15 — tutorial hand on board (port 1:1); gated on hadFirstInteraction + skin.slots.tutorialHand.
  const tutorialFirstMove = payload.level.script?.firstMove;
  const tutorialHandSprite = !hadFirstInteraction && payload.skin.slots.tutorialHand.length > 0 && tutorialFirstMove !== undefined
    ? (() => {
        const handIdx = tutorialFirstMove.a.row * payload.level.board.cols + tutorialFirstMove.a.col;
        const cell = cellByIndex.get(handIdx);
        if (!cell) return null;
        const handSize = viewModel.layout.board.cellSize * 1.15;
        return (
          <NativeSprite
            id="match-tutorial-hand"
            assetUrl={payload.skin.slots.tutorialHand}
            x={cell.center.x + cell.visual.width * 0.18}
            y={cell.center.y + cell.visual.height * 0.34}
            width={handSize}
            height={handSize}
            anchor={{ x: 0.3, y: 0.2 }}
            visible
            registerTarget={registerNativeTarget}
            onLayerSelected={onLayerSelected}
          />
        );
      })()
    : null;

  return (
    <pixiContainer x={0} y={0} visible>
      {backgroundSprite}
      {ambientSpritesJsx}
      {boardPanelGraphic}
      {cellSocketUnderlays}
      {tileShadowLayer}
      {underlaySprites}
      {tileSprites}
      {specialOverlays}
      {staticSprites}
      {collectFlightSprites}
      {clearFxSpritesJsx}
      {blastFxSpritesJsx}
      {comboPopupJsx}
      {tutorialHandSprite}
      {bannerSprite}
      {hudPill}
      {hudIcon}
      {hudCounter}
      {titleText}
      {endcardBackdrop}
      {endcardSprite}
      {confettiGraphic}
      {ctaSprite}
      {ctaLabel}
    </pixiContainer>
  );
}
